/* LAV TDMS — Transmittals (log + TRM builder) and distribution-matrix Review. */
import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import type { ReactNode, CSSProperties } from 'react'
import {
  Button,
  Input,
  Select,
  Badge,
  Checkbox,
  Avatar,
  PageHead,
  Purpose,
  CodeChip,
  DiscTag,
  SlaPill,
  EmptyState,
  Toolbar,
  ToolSpacer,
  DateInput,
  Segmented,
  SmartTable, clickable, toast, ResizeHandles, useResizable, railWidth, Spinner } from '@/ui'
import type { SmartColumn, Tone } from '@/ui'
import { Icon } from '@/ds/icons'
import { colHeader } from '@/i18n/columns'
import { useQueryClient } from '@tanstack/react-query'
import { qk, useFacets, useList, useMeta, useSub, inboxKeys } from '@/data/queries'
import { fetchList, noteExport, saveFile, uploadForm } from '@/data/api'
import { exportTransmittals } from '@/lib/excel'
import { useCommand } from '@/data/mutations'
import { useDictLabels, useDictRows, useMe } from '@/data/meta'
import { Pager } from '@/screens/pt/Registers'
import { CRIT_LABEL, CritBadge, SheetPrint } from '@/screens/pt/CrsSheet'
import { TrmFilesZone } from '@/screens/pt/TrmFiles'
import { CodeLink } from '@/screens/pt/DocumentCard'
import type { Doc, Mdr, Purpose as PurposeCode, ReviewCode, Transmittal, Vdr, Vendor } from '@/data/types'
import { revVer } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'


// ---- prototype DS primitives (inline-styled, pixel-faithful) ------------
function Tabs({
  tabs = [],
  value,
  onChange,
  style = {},
}: {
  tabs: Array<{ id: string; label: ReactNode; count?: number } | string>
  value?: string
  onChange?: (id: string) => void
  style?: CSSProperties
}) {
  return (
    <div role="tablist" style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: 'var(--border-hairline) solid var(--border-default)', ...style }}>
      {tabs.map((tab) => {
        const id = typeof tab === 'string' ? tab : tab.id
        const label = typeof tab === 'string' ? tab : tab.label
        const count = typeof tab === 'string' ? undefined : tab.count
        const active = id === value
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange && onChange(id)}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-5) var(--space-5)',
              marginBottom: -1,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              font: 'var(--font-ui)',
              fontSize: 'var(--text-md)',
              fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
              color: active ? 'var(--text-strong)' : 'var(--text-muted)',
              borderBottom: `var(--border-accent) solid ${active ? 'var(--accent)' : 'transparent'}`,
              // Подчёркивание переходит вместе с цветом: порознь текст
              // перетекает, а полоса прыгает.
              transition: 'color var(--duration-fast) var(--ease-standard), border-bottom-color var(--duration-fast) var(--ease-standard)',
            }}
          >
            {label}
            {count != null ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 'var(--weight-medium)',
                  color: active ? 'var(--accent-ink)' : 'var(--text-subtle)',
                  background: active ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                  border: `var(--border-hairline) solid ${active ? 'var(--accent-line)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '1px var(--space-3)',
                }}
              >
                {count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function Dialog({
  open,
  onClose,
  title,
  subtitle,
  width = 520,
  /* Под каким ключом запоминается размер. На экране их теперь четыре, и один
     ключ на всех означал бы, что растянутый лист CRS растягивает и окошко
     причины отклонения. */
  sizeKey = 'trm-create',
  footer,
  children,
  style = {},
}: {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  subtitle?: ReactNode
  width?: number
  sizeKey?: string
  footer?: ReactNode
  children?: ReactNode
  style?: CSSProperties
}) {
  const box = useResizable({ sizeKey, width, edges: ['left', 'right', 'bottom', 'bottom-left', 'bottom-right'] })
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div
      role="presentation"
      /* Вход как у DlgShell реестров: fade на подложке, dialog-pop подхватывает
         вложенный [role='dialog'] селектором ui.css. Уход мгновенный намеренно —
         паритет с DlgShell. */
      className="dlg-shell"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onClose) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)' as CSSProperties['zIndex'],
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-16)',
        background: 'rgba(14,17,22,0.45)',
        backdropFilter: 'blur(1px)',
      }}
    >
      <div
        ref={box.ref}
        role="dialog"
        aria-modal="true"
        /* Ограничение высоты считается вместе с отступами подложки: подложка во
           весь экран с padding var(--space-16), и 90vh внутри неё давали 90vh + 64 —
           на экране 768 окно вылезало нижней кромкой. Вызывающий стиль разлит ДО
           ограничений: своё оформление он задавать вправе, а снести границы
           экрана — нет. */
        style={{
          position: 'relative',
          width: box.width,
          height: box.height ?? undefined,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-raised)',
          border: 'var(--border-hairline) solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          ...style,
          maxWidth: 'calc(100vw - var(--space-16) * 2)',
          maxHeight: 'calc(100vh - var(--space-16) * 2)',
        }}
      >
        <ResizeHandles box={box} />
        {title ? (
          <header style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-6)', padding: 'var(--space-8) var(--space-10)', borderBottom: 'var(--border-hairline) solid var(--border-subtle)' }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{title}</h2>
              {subtitle ? <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{subtitle}</div> : null}
            </div>
            <button
              onClick={onClose}
              aria-label="Закрыть"
              /* icon-btn даёт нажатию scale и transition (паритет с «✕» реестров);
                 hover-фон инлайновый background всё равно глушит — осознанно. */
              className="icon-btn"
              style={{ marginLeft: 'auto', display: 'grid', placeItems: 'center', width: 28, height: 28, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer', fontSize: '16px', borderRadius: 'var(--radius-sm)' }}
            >
              {'✕'}
            </button>
          </header>
        ) : null}
        <div style={{ padding: 'var(--space-10)', overflow: 'auto', font: 'var(--font-body)', fontSize: 'var(--text-md)', color: 'var(--text-body)' }}>{children}</div>
        {footer ? (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)', padding: 'var(--space-8) var(--space-10)', borderTop: 'var(--border-hairline) solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>{footer}</footer>
        ) : null}
      </div>
    </div>
  )
}

const TRM_STATUS: Record<string, Tone> = {
  'Зарегистрирован': 'info', 'На рассмотрении': 'accent', 'Замечания': 'warning',
  'Отправлен': 'info', 'Закрыт': 'success', 'Отклонён': 'danger',
}
function TrmStatus({ s }: { s: string }) { return <Badge tone={TRM_STATUS[s] ?? 'neutral'} size="sm" dot>{s}</Badge> }
function DirChip({ dir }: { dir: string }) {
  const inb = dir === 'in'
  return <span title={inb ? 'Входящий' : 'Исходящий'} style={{ display: 'inline-grid', placeItems: 'center', width: 24, height: 24, borderRadius: 'var(--radius-sm)', background: inb ? 'var(--blue-50)' : 'var(--amber-50)', color: inb ? 'var(--blue-600)' : 'var(--amber-600)', border: '1px solid ' + (inb ? 'var(--blue-100)' : 'var(--amber-100)') }}><Icon name={inb ? 'arrow-down-left' : 'arrow-up-right'} size={14} /></span>
}

// ---- Transmittals log --------------------------------------------------
/** Размер страницы журнала. Сервер отдаёт ровно эту порцию, а не весь журнал. */
const PAGE_SIZE = 50
/* Порция выгрузки. Совпадает с потолком страницы на сервере
   (PageResponse.MAX_PAGE_SIZE): просить больше бессмысленно — обрежет. */
const EXPORT_CHUNK = 200

/* onOpenDoc принимается для совместимости с вызывающим кодом, но внутри не
   используется. */
export function Transmittals({ lang = 'ru', vendor, onOpenTrm }: { lang?: Lang; vendor?: string | null; onOpenDoc?: (no: string) => void; onOpenTrm?: (id: string) => void }) {
  const [dir, setDir] = useState('all')
  /* Переход с экрана организаций приходит с именем контрагента: у пакета нет
     колонки подрядчика — стороны записаны отправителем и получателем, и они же
     ищутся строкой поиска (searchable: from, to). */
  const [q, setQ] = useState(vendor ?? '')
  const [page, setPage] = useState(0)
  useEffect(() => { setQ(vendor ?? ''); setPage(0) }, [vendor])
  const [builder, setBuilder] = useState(false)
  const [incoming, setIncoming] = useState(false)

  // Журнал приходит с сервера: направление, поиск и разбиение на страницы
  // выполняются там же (см. src/data/contracts.ts).
  const [pending, setPending] = useState(false)
  const trmQuery = useList<Transmittal>('transmittals', {
    page,
    pageSize: PAGE_SIZE,
    sort: [{ id: 'id', desc: false }],
    filters: { ...(dir === 'all' ? {} : { dir: [dir] }), ...(pending ? { acknowledged: ['empty:'] } : {}) },
    q: q || undefined,
  })
  const rows = trmQuery.data?.rows ?? []

  // Счётчики вкладок считает сервер по всему журналу: по странице они показывали
  // бы число видимых строк вместо числа трансмитталов.
  const dirFacets = useFacets('transmittals', ['dir'])
  const dirCount = (v: string): number | undefined =>
    dirFacets.isSuccess ? (dirFacets.data.dir?.find((b) => b.value === v)?.count ?? 0) : undefined
  const total = dirFacets.isSuccess ? (dirFacets.data.dir ?? []).reduce((n, b) => n + b.count, 0) : undefined

  const me = useMe()
  // Смена фильтра сбрасывает страницу: иначе на второй странице нового набора
  // легко оказаться за его пределами.
  const setFilter = (apply: () => void) => { apply(); setPage(0) }

  /* Выгрузка журнала берёт весь отфильтрованный набор, а не показанную
     страницу: «журнал в Excel» должен означать журнал. Порциями по потолку
     страницы сервера — запрос «дай сразу все» молча вернул бы первые 200. */
  const exportLog = async () => {
    const filters: Record<string, string[]> = {
      ...(dir === 'all' ? {} : { dir: [dir] }),
      ...(pending ? { acknowledged: ['empty:'] } : {}),
    }
    const all: Transmittal[] = []
    let p = 0
    let pageCount = 1
    do {
      const chunk = await fetchList<Transmittal>('transmittals', {
        page: p, pageSize: EXPORT_CHUNK, sort: [{ id: 'id', desc: false }], filters, q: q || undefined,
      })
      all.push(...chunk.rows)
      pageCount = chunk.pageCount
      p += 1
    } while (p < pageCount)
    exportTransmittals(all, 'TRM_journal.xlsx', lang)
    /* Выгрузка — событие журнала: файл собрал браузер, а «кто унёс переписку
       проекта» обязано быть видно. */
    noteExport('журнал трансмитталов', q || undefined, all.length)
  }
  const runExport = () => {
    exportLog().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }

  const cols: SmartColumn<Transmittal>[] = [
    { key: 'dir', header: '', width: 40, render: (r) => <DirChip dir={r.dir} />, value: (r) => r.dir === 'in' ? 'Входящий' : 'Исходящий', groupable: true, filterable: true, sortable: false },
    { key: 'id', header: '№ TRM', mono: true, width: 96, render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{r.id}</span> },
    { key: 'subject', header: colHeader(lang, 'Содержание'), render: (r) => (
      <div><div style={{ color: 'var(--text-body)' }}>{r.subject}</div><div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 1 }}>{r.from} → {r.to}</div></div>
    )},
    /* Стороны передачи — свои колонки: по отправителю и получателю журнал
       сортируют и группируют, а подпись под темой для этого не годится. */
    { key: 'from', header: colHeader(lang, 'От кого направлен'), width: 150, groupable: true, filterable: true,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.from}</span> },
    { key: 'to', header: colHeader(lang, 'Кому направлен'), width: 150, groupable: true, filterable: true,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.to}</span> },
    { key: 'purpose', header: colHeader(lang, 'Назн.'), width: 60, filterable: true, valueLabel: (v) => v || '—', render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'docs', header: colHeader(lang, 'Док.'), align: 'center', mono: true, width: 56, render: (r) => r.docs },
    { key: 'date', header: colHeader(lang, 'Дата'), mono: true, align: 'right', width: 86, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.date}</span> },
    { key: 'due', header: colHeader(lang, 'Срок'), mono: true, align: 'right', width: 86, render: (r) => r.due ? <span style={{ color: 'var(--date-forecast)' }}>{r.due}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span> },
    { key: 'status', header: colHeader(lang, 'Статус'), width: 150, groupable: true, filterable: true, render: (r) => <TrmStatus s={r.status} /> },
    { key: 'code', header: colHeader(lang, 'Код'), align: 'center', width: 50, filterable: true, valueLabel: (v) => v || '—', render: (r) => <CodeChip code={r.code} size="sm" /> },
    /* Передача — факт с двумя сторонами, и журнал обязан отвечать «дошло ли»,
       а не «отправили ли». Отметку писали с самого начала и нигде не
       показывали: неподтверждённые передачи не видел никто. */
    { key: 'acknowledged', header: colHeader(lang, 'Получен'), mono: true, align: 'right', width: 96,
      render: (r) => r.acknowledged
        ? <span style={{ color: 'var(--success-ink)' }} title={r.acknowledgedBy ?? undefined}>{r.acknowledged}</span>
        : <span style={{ color: 'var(--text-subtle)' }}>—</span> },
  ]
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Трансмитталы', 'Transmittals')}</span>}
        title={t(lang, 'Трансмитталы', 'Transmittals')} count={total}
        actions={<><Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />} onClick={runExport}>{t(lang, 'Журнал в Excel', 'Export log')}</Button>
          {/* Пакет приходит и почтой, и на диске: регистрирует его координатор,
              называя отправителя и дату получения. */}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="inbox" size={14} />} onClick={() => setIncoming(true)}>{t(lang, 'Зарегистрировать входящий', 'Register incoming')}</Button>
          <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setBuilder(true)}>{t(lang, 'Сформировать TRM', 'Create TRM')}</Button></>}
        tabsNode={<Tabs value={dir} onChange={(id) => setFilter(() => setDir(id))} tabs={[{ id: 'all', label: t(lang, 'Все', 'All'), count: total }, { id: 'in', label: t(lang, 'Входящие', 'Incoming'), count: dirCount('in') }, { id: 'out', label: t(lang, 'Исходящие', 'Outgoing'), count: dirCount('out') }]} />}
      />
      <Toolbar>
        <div style={{ width: 280 }}><Input size="sm" value={q} onChange={(e) => setFilter(() => setQ(e.target.value))} leading={<Icon name="search" size={14} />} placeholder={t(lang, 'Поиск по № TRM, теме, отправителю…', 'Search…')} /></div>
        {/* Неподтверждённые передачи: пакет ушёл, а получение никто не
            отметил. До сих пор их не видел никто — ни колонкой, ни отбором. */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}
               title={t(lang, 'Показать только пакеты, получение которых не подтверждено',
                             'Show only packages whose receipt is not acknowledged')}>
          <input type="checkbox" checked={pending} onChange={(e) => setFilter(() => setPending(e.target.checked))} />
          {t(lang, 'Не подтверждены', 'Not acknowledged')}
        </label>
        <ToolSpacer />
        <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'Документ передаётся только в составе TRM', 'Documents move only inside a TRM')}</span>
      </Toolbar>
      <div style={{ flex: 1, minHeight: 0 }}>
        {trmQuery.isError
          ? <div style={{ padding: 20, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить трансмитталы', 'Failed to load transmittals')}</div>
          : <SmartTable name="transmittals" columns={cols} rows={rows} rowKey={(r) => r.id} lang={lang} onRowClick={(r) => onOpenTrm && onOpenTrm(r.id)} loading={trmQuery.isPending} emptyText={t(lang, 'Нет трансмитталов', 'No transmittals')} />}
      </div>
      <Pager page={trmQuery.data?.page ?? 0} pageCount={trmQuery.data?.pageCount ?? 1} total={trmQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
      <TrmBuilder open={builder} onClose={() => setBuilder(false)} lang={lang} />
      <IncomingTrmDialog open={incoming} onClose={() => setIncoming(false)} lang={lang} />
    </div>
  )
}

/**
 * Регистрация входящего пакета стороной заказчика.
 *
 * <p>Подрядчик может вовсе не работать в портале: пакет приходит почтой,
 * курьером, на диске. Отправитель называется явно, дата — фактическая дата
 * поступления, а не сегодняшняя: от неё считается срок ответа.
 */
function IncomingTrmDialog({ open, onClose, lang }: { open: boolean; onClose: () => void; lang: Lang }) {
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'name', desc: false }] }, { enabled: open })
  const vendors = vendorsQuery.data?.rows ?? []
  const [chosenSender, setChosenSender] = useState('')
  const sender = chosenSender || vendors[0]?.name || ''
  const [issuedOn, setIssuedOn] = useState('')
  const [partnerNo, setPartnerNo] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState('')
  /* Входящее письмо без состава. Регламент начинает круг с того, что подрядчик
     присылает ЗАПОЛНЕННЫЙ файл реестра: карточек документов ещё нет, и выбирать
     в составе нечего — а зарегистрировать приход обязаны, с номером и датой.
     Сервер такую подачу принимает признаком «письмо» (requireDocs → isLetter),
     и исходящий пакет им пользуется давно; входящему признак не давали, и шаг
     регламента упирался в «Выберите хотя бы один документ». */
  const [letter, setLetter] = useState(false)
  const [subject, setSubject] = useState('')

  /* Состав — документы этого подрядчика, которые он вправе подать: правило то
     же, что у портала, и сервер отбивает всё остальное. */
  const docsQuery = useList<Doc>(
    'docs',
    /* «Принят с замечаниями» подаётся так же, как «ожидает переиздания»:
       код B закрывает круг, а замечания учитываются следующей версией. Без
       него документ с самым частым исходом рассмотрения не попадал в состав
       подачи вовсе. Набор тот же, что SUBMITTABLE на сервере. */
    { page: 0, pageSize: 200, sort: [{ id: 'no', desc: false }], filters: { vendor: [sender], life: ['planned', 'reissue', 'approved', 'accepted'] } },
    { enabled: open && sender !== '' },
  )
  const docs = docsQuery.data?.rows ?? []
  /* Назначение — свойство документа: цель подачи каждому документу назначает
     схема (V97), поле пакета убрано. Сервер отдаёт, куда поведёт каждый
     документ, — этим подписывается колонка «Будет назначено». */
  const flow = useSub<{ docs: { no: string; target: string | null; final: boolean }[] }>(
    open && !letter && docs.length
      ? '/transmittals/inbound/purposes?docs=' + encodeURIComponent(docs.map((d) => d.no).join(','))
      : null,
  )
  const flowByNo = useMemo(() => {
    const m = new Map<string, { target: string | null; final: boolean }>()
    for (const row of flow.data?.docs ?? []) m.set(row.no, row)
    return m
  }, [flow.data])
  const needle = filter.trim().toLowerCase()
  const shown = needle ? docs.filter((d) => (d.no + ' ' + d.title).toLowerCase().includes(needle)) : docs
  const toggle = (no: string) => { const n = new Set(picked); if (n.has(no)) n.delete(no); else n.add(no); setPicked(n) }

  const register = useCommand<{ sender: string; docNos: string[]; issuedOn: string; partnerNo: string
                               letter?: boolean; subject?: string },
    { id: string; docs: number }>({
    path: () => '/transmittals/inbound',
    body: (a) => a,
    invalidate: () => [qk.all('transmittals'), qk.all('docs'), qk.all('vdocs'), qk.all('audit'), ...inboxKeys],
  })
  return (
    <Dialog open={open} onClose={onClose} width={720} title={t(lang, 'Зарегистрировать входящий пакет', 'Register an incoming package')}
      subtitle={t(lang, 'Пакет пришёл почтой, курьером или на диске', 'The package arrived by mail, courier or on a disk')}
      footer={<><Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={(letter ? subject.trim() === '' : picked.size === 0) || register.isPending} iconLeft={<Icon name="inbox" size={14} />}
                title={letter
                  ? (subject.trim() ? undefined : t(lang, 'У письма без вложений предмет один — тема', 'A letter without attachments has only its subject'))
                  : (picked.size ? undefined : t(lang, 'Выберите хотя бы один документ', 'Pick at least one document'))}
                onClick={() => register.mutate({ sender, docNos: letter ? [] : [...picked], issuedOn, partnerNo,
                                                 ...(letter ? { letter: true, subject: subject.trim() } : {}) }, {
                  onSuccess: (res) => {
                    setPicked(new Set())
                    onClose()
                    toast({ tone: 'success', title: `${res.id} ${t(lang, 'зарегистрирован', 'registered')}`,
                      text: t(lang, `${sender} · документов ${res.docs}`, `${sender} · ${res.docs} document(s)`) })
                  },
                })}>{register.isPending ? t(lang, 'Регистрируем…', 'Registering…') : t(lang, 'Зарегистрировать', 'Register')}{letter ? '' : ' · ' + picked.size}</Button></>}>
      {/* Признак называется явно, а не выводится из пустого состава: «забыл
          выбрать документы» и «письмо без вложений» — разные события. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          <input type="checkbox" checked={letter} onChange={(e) => setLetter(e.target.checked)} />
          {t(lang, 'Письмо без вложений', 'Letter without attachments')}
        </label>
        {letter ? (
          <div style={{ flex: 1 }}>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   placeholder={t(lang, 'Тема письма — например: заполненный шаблон реестра MDR-C-999',
                                        'Subject — e.g. the filled register template MDR-C-999')} />
          </div>
        ) : (
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {t(lang, 'заполненный шаблон реестра, ответ на письмо, сопроводительное — приходят без карточек документов',
                     'a filled register template, a reply, a cover letter — they arrive without document cards')}
          </span>
        )}
      </div>
      {/* Назначения выпуска здесь больше нет: цель — свойство документа, и
          при регистрации её назначает схема (колонка «→» в списке). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <Select label={t(lang, 'Отправитель', 'Sender')} value={sender} onChange={(e) => { setChosenSender(e.target.value); setPicked(new Set()) }}>{vendors.map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}</Select>
        <Input label={t(lang, 'Дата получения', 'Received on')} value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} placeholder="ДД.ММ.ГГГГ" />
        <Input label={t(lang, '№ отправителя', 'Sender’s no')} value={partnerNo} onChange={(e) => setPartnerNo(e.target.value)} placeholder={t(lang, 'номер письма подрядчика', 'contractor letter no')} />
      </div>
      <div style={{ display: letter ? 'none' : 'block', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Документы подрядчика', 'Contractor documents')}</span>
          <div style={{ width: 240 }}><Input size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} leading={<Icon name="search" size={13} />} placeholder={t(lang, 'Поиск…', 'Search…')} /></div>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{picked.size} {t(lang, 'выбрано', 'selected')}</span>
        </div>
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          {shown.map((d) => {
            const f = flowByNo.get(d.no)
            return (
              <label key={d.no} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                <Checkbox checked={picked.has(d.no)} onChange={() => toggle(d.no)} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', width: 150 }}>{d.no}</span>
                <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{d.rev}</span>
                {/* Куда схема поведёт документ при регистрации подачи. */}
                <span style={{ width: 90, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Purpose value={d.purpose} size="sm" />
                  {f?.target && f.target !== d.purpose ? (
                    <>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>→</span>
                      <Purpose value={f.target} size="sm" />
                    </>
                  ) : null}
                </span>
              </label>
            )
          })}
          {docs.length === 0 ? (
            <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {t(lang, 'У этого подрядчика нет документов, готовых к подаче', 'This contractor has no documents ready to submit')}
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 10 }}>
        {t(lang, 'Сервер проверит то же, что и у портала: свои документы подрядчика, состояние «планируется» или «переиздание», наличие основного файла.',
          'The server checks the same rules as the portal: the contractor’s own documents, state planned or reissue, and a main file present.')}
      </div>
    </Dialog>
  )
}

// ---- TRM builder dialog ------------------------------------------------
function TrmBuilder({ open, onClose, lang }: { open: boolean; onClose: () => void; lang: Lang }) {
  /* Списки читаются с сервера и только при открытом диалоге: иначе журнал на
     каждом заходе тянул бы ещё и реестры с документами. */
  const mdrsQuery = useList<Mdr>('mdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] }, { enabled: open })
  const mdrs = mdrsQuery.data?.rows ?? []
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'name', desc: false }] }, { enabled: open })
  const vendors = vendorsQuery.data?.rows ?? []

  /* Источник состава: свой реестр MDR или реестр подрядчика VDR. Передать
     смежнику документы, полученные от подрядчика (назначение IFI), сервер
     позволял с самого начала, а собрать такой пакет было негде: экран
     показывал только свои документы. */
  const [source, setSource] = useState<'mdr' | 'vdr'>('mdr')
  const vdrsQuery = useList<Vdr>('vdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] }, { enabled: open && source === 'vdr' })
  const [chosenRecipient, setChosenRecipient] = useState('')
  const recipient = chosenRecipient || vendors[0]?.name || ''
  /* Реестры — только выбранного получателя: пакет собирают контрагенту, и
     реестры чужих подрядчиков в списке — путь к письму не тому. Реестр без
     организации (свой, заказчика) остаётся доступен всем адресатам. */
  const mdrsOf = mdrs.filter((m) => !m.vendor || m.vendor === recipient)
  const vdrs = (vdrsQuery.data?.rows ?? []).filter((v) => !v.vendor || v.vendor === recipient)
  // Выбор встаёт на первую пришедшую строку, а не на значение из seed.
  const [chosenMdr, setChosenMdr] = useState('')
  const mdr = chosenMdr || mdrsOf[0]?.id || ''
  const [chosenVdr, setChosenVdr] = useState('')
  const vdr = chosenVdr || vdrs[0]?.id || ''
  const [picked, setPicked] = useState<Set<string>>(new Set())
  /* Черновик: номер выделен, письмо ещё правится. Между «собрал» и «отправил»
     стоит подпись, и до неё пакет не уходит никому. */
  const [draft, setDraft] = useState(false)
  const [fao, setFao] = useState('')
  /* Копии, полномочия и отметки — колонки бланка заказчика. Сервер принимал их
     с V47, а набрать их было негде: письмо уходило без копий и без отметок,
     которые в нём печатаются. */
  const [cc, setCc] = useState('')
  const [authority, setAuthority] = useState('')
  const [remarks, setRemarks] = useState('')
  const [due, setDue] = useState('')
  /* Носитель и число экземпляров: колонки бланка, которые прежде уходили
     пустыми и дописывались от руки. Задаются на весь состав, а расходящуюся
     позицию правят в карточке письма. */
  const [medium, setMedium] = useState<'el' | 'paper' | 'both'>('el')
  const [copies, setCopies] = useState('1')
  /* Письмо без вложений: напоминание о просрочке, запрос исходных данных,
     согласование продления срока. Прежде такие письма уходили почтой мимо
     системы — выпустить их было нельзя, состав был обязателен. Предмет письма
     называется темой, и она обязательна: у него нет другого предмета. */
  const [letter, setLetter] = useState(false)
  const [subject, setSubject] = useState('')

  const docsQuery = useList<Doc>(
    source === 'vdr' ? 'vdocs' : 'docs',
    { page: 0, pageSize: 200, sort: [{ id: 'no', desc: false }],
      filters: source === 'vdr' ? { vdr: [vdr] } : { mdr: [mdr] } },
    { enabled: open && (source === 'vdr' ? vdr !== '' : mdr !== '') },
  )
  const docs = docsQuery.data?.rows ?? []
  const toggle = (no: string) => { const n = new Set(picked); if (n.has(no)) n.delete(no); else n.add(no); setPicked(n) }
  /* Поиск сужает список, но не выбор: отмеченный документ, скрытый фильтром,
     остаётся в составе — счётчик и кнопка считают picked, а не показанное. */
  const [filter, setFilter] = useState('')
  const needle = filter.trim().toLowerCase()
  const shown = needle ? docs.filter((d) => (d.no + ' ' + d.title).toLowerCase().includes(needle)) : docs

  /* Листы CRS выбранных документов, ещё не ушедшие исходящим и совпадающие
     ревизией: исходящий TRM несёт реестр замечаний к конкретному документу
     конкретной версии (замечание 10). Поле trmOut фильтруемо, контракт цел. */
  const sheetsQuery = useList<{ id: string; doc: string; rev: string | null; ver: number | null; code: ReviewCode; trmOut: string | null }>(
    'crs-sheets',
    { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }], filters: picked.size ? { doc: [...picked] } : {} },
    { enabled: open && picked.size > 0 },
  )
  const availableSheets = (sheetsQuery.data?.rows ?? []).filter((s) =>
    !s.trmOut && docs.some((d) => d.no === s.doc && d.rev === s.rev))
  const [sheets, setSheets] = useState<Set<string>>(new Set())
  const toggleSheet = (code: string) => { const n = new Set(sheets); if (n.has(code)) n.delete(code); else n.add(code); setSheets(n) }
  // Снятый документ уносит свой лист из отправки, даже если галочка осталась.
  const chosenSheets = [...sheets].filter((code) => availableSheets.some((s) => s.id === code))

  /* Тема, номер, срок и область трансмиттала считает сервер по составу, поэтому
     команда несёт только выбор человека. Пустой состав сервер отбивает кодом
     trm.docs-required; кнопка гаснет раньше, чтобы не слать заведомый отказ. */
  const create = useCommand<{ recipient: string; docNos: string[]; sheetCodes: string[];
                             draft: boolean; fao: string; due: string
                             cc: string; authority: string; remarks: string
                             medium: string; copies: number
                             letter?: boolean; subject?: string },
    { id: string; status: string; docs: number }>({
    path: () => '/transmittals',
    body: (a) => a,
    invalidate: () => [qk.all('transmittals'), qk.all('docs'), qk.all('crs-sheets'), qk.all('audit'), ...inboxKeys],
  })
  /* Номер называется в уведомлении: журнал постраничный и отсортирован по
     номеру, так что свежий трансмиттал легко окажется не на видимой странице. */
  const submit = () => create.mutate({ recipient,
                                      docNos: letter ? [] : [...picked],
                                      sheetCodes: letter ? [] : chosenSheets,
                                      draft, fao: fao.trim(), due: due.trim(),
                                      cc: cc.trim(), authority: authority.trim(), remarks: remarks.trim(),
                                      medium, copies: Math.min(999, Math.max(1, Number.parseInt(copies, 10) || 1)),
                                      ...(letter ? { letter: true, subject: subject.trim() } : {}) }, {
    onSuccess: (res) => {
      setPicked(new Set())
      onClose()
      toast({
        tone: 'success',
        title: `${res.id} ${t(lang, 'отправлен', 'sent')}`,
        text: t(lang, `${recipient} · документов ${res.docs}`, `${recipient} · ${res.docs} document(s)`),
      })
    },
  })
  return (
    <Dialog open={open} onClose={onClose} width={720} title={t(lang, 'Сформировать исходящий TRM', 'Create outgoing TRM')} subtitle={t(lang, 'Выберите документы: назначение у каждого своё — оно стоит в карточке. Документы подрядчика уходят смежнику как IFI.',
                        'Pick documents: each carries its own purpose from the card. Contractor documents go to a third party as IFI.')}
      footer={<><Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button><Button variant="primary" disabled={(letter ? subject.trim() === '' : picked.size === 0) || create.isPending} title={letter ? (subject.trim() ? undefined : t(lang, 'У письма без вложений предмет один — тема', 'A letter without attachments has only its subject')) : (picked.size ? undefined : t(lang, 'Выберите хотя бы один документ', 'Pick at least one document'))} iconLeft={<Icon name="send" size={14} />} onClick={submit}>{create.isPending ? t(lang, 'Сохраняем…', 'Saving…') : draft ? t(lang, 'Сохранить черновик', 'Save draft') : t(lang, 'Создать и отправить', 'Create & send')}{letter ? '' : ' · ' + picked.size}</Button></>}>
      {/* Письмо без вложений — отдельный вид отправления, а не забытый состав:
          «забыл приложить» обязано отбиваться по-прежнему. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          <input type="checkbox" checked={letter} onChange={(e) => setLetter(e.target.checked)} />
          {t(lang, 'Письмо без вложений', 'Letter without attachments')}
        </label>
        {letter ? (
          <div style={{ flex: 1 }}>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                   placeholder={t(lang, 'Тема письма — например: о продлении срока рассмотрения TRM-0042',
                                        'Subject — e.g. on extending the review period for TRM-0042')} />
          </div>
        ) : (
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {t(lang, 'напоминание, запрос исходных данных, ответ на письмо — уходят с номером и в журнал',
                     'a reminder, a request for input data, a reply — they get a number and go into the log')}
          </span>
        )}
      </div>
      {/* Назначения выпуска у письма больше нет: цель — свойство документа,
          письмо получает общую цель состава само, когда она одна. */}
      <div style={{ display: 'grid', gridTemplateColumns: letter ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        {letter ? null : <><Select label={t(lang, 'Источник состава', 'Source')} value={source}
                onChange={(e) => { setSource(e.target.value as 'mdr' | 'vdr'); setPicked(new Set()) }}>
          <option value="mdr">{t(lang, 'Реестр MDR — свои документы', 'MDR — own documents')}</option>
          <option value="vdr">{t(lang, 'Реестр VDR — документы подрядчика', 'VDR — contractor documents')}</option>
        </Select>
        {source === 'vdr'
          ? <Select label={t(lang, 'Реестр VDR', 'VDR')} value={vdr} onChange={(e) => { setChosenVdr(e.target.value); setPicked(new Set()) }}
                    hint={vdrs.length ? undefined : t(lang, 'У получателя нет реестров VDR', 'The recipient has no VDR registers')}>
              {vdrs.map((v) => <option key={v.id} value={v.id}>{v.id}</option>)}
            </Select>
          : <Select label={t(lang, 'Реестр MDR', 'MDR')} value={mdr} onChange={(e) => { setChosenMdr(e.target.value); setPicked(new Set()) }}
                    hint={mdrsOf.length ? undefined : t(lang, 'У получателя нет реестров MDR', 'The recipient has no MDR registers')}>
              {mdrsOf.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
            </Select>}</>}
        {/* Смена получателя меняет и доступные реестры: выбор состава с ней сбрасывается. */}
        <Select label={t(lang, 'Получатель', 'Recipient')} value={recipient} onChange={(e) => { setChosenRecipient(e.target.value); setChosenMdr(''); setChosenVdr(''); setPicked(new Set()) }}>{vendors.map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}</Select>
      </div>
      {/* Реквизиты письма: кому лично и срок ответа. Срок — предмет
          договорённости, а не сумма SLA внутренней матрицы. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, marginBottom: 14, alignItems: 'end' }}>
        <Input label={t(lang, 'Кому лично (FAO)', 'For attention of')} value={fao} onChange={(e) => setFao(e.target.value)} placeholder={t(lang, 'необязательно', 'optional')} />
        <Input label={t(lang, 'Срок ответа', 'Response due')} value={due} onChange={(e) => setDue(e.target.value)} placeholder="ДД.ММ.ГГГГ" />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', paddingBottom: 8 }}>
          <input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)} />
          {t(lang, 'Черновик', 'Draft')}
        </label>
      </div>
      {/* Копии, полномочия и отметки: их печатает бланк заказчика, а набрать
          их было негде — сервер принимал эти поля, но форма о них не знала. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {/* Организация проекта, названная в копии, получает доступ к пакету и
            извещение о нём: копия — участник переписки, а не строка в бланке.
            Адресат вне проекта остаётся просто текстом бланка. */}
        <Input label={t(lang, 'Копии (CC)', 'CC')} value={cc} onChange={(e) => setCc(e.target.value)}
               placeholder={t(lang, 'организации через запятую', 'organisations, comma separated')}
               hint={t(lang, 'Организация проекта в копии увидит пакет и получит извещение',
                             'A project organisation in CC gets access and a notification')} />
        <Input label={t(lang, 'Полномочия', 'Authority')} value={authority} onChange={(e) => setAuthority(e.target.value)}
               placeholder={t(lang, 'необязательно', 'optional')} />
        <Select label={t(lang, 'Носитель', 'Medium')} value={medium} onChange={(e) => setMedium(e.target.value as 'el' | 'paper' | 'both')}>
          <option value="el">{t(lang, 'Электронно', 'Electronic')}</option>
          <option value="paper">{t(lang, 'Бумага', 'Paper')}</option>
          <option value="both">{t(lang, 'Бумага и эл.', 'Paper & el.')}</option>
        </Select>
        <Input label={t(lang, 'Экземпляров', 'Copies')} value={copies} onChange={(e) => setCopies(e.target.value)}
               hint={t(lang, 'Печатается в описи бланка', 'Printed in the blank composition')} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Input label={t(lang, 'Отметки в письме', 'Remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                 placeholder={t(lang, 'что напечатать в бланке', 'what to print on the blank')} />
        </div>
      </div>
      <div style={{ display: letter ? 'none' : 'block', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{t(lang, 'Документы', 'Documents')}</span>
          <div style={{ width: 240 }}><Input size="sm" value={filter} onChange={(e) => setFilter(e.target.value)} leading={<Icon name="search" size={13} />} placeholder={t(lang, 'Поиск по документу…', 'Search documents…')} /></div>
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{picked.size} {t(lang, 'выбрано', 'selected')}</span>
        </div>
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          {shown.map((d) => (
            <label key={d.no} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
              <Checkbox checked={picked.has(d.no)} onChange={() => toggle(d.no)} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-strong)', width: 130 }}>{d.no}</span>
              <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>{d.rev}</span>
              <Purpose value={d.purpose} size="sm" />
            </label>
          ))}
          {docs.length > 0 && shown.length === 0 ? (
            <div style={{ padding: '14px 12px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Ничего не найдено', 'Nothing found')}</div>
          ) : null}
        </div>
      </div>
      <TrmFilesZone docs={docs.filter((d) => picked.has(d.no)).map((d) => ({ no: d.no, rev: d.rev }))} lang={lang} disabled={create.isPending} />
      {/* Кнопка «прикрепить CRS» из замечания 10: доступные листы выбранных
          документов, ревизия в ревизию; ушедшие исходящим не предлагаются. */}
      {availableSheets.length ? (
        <div style={{ marginTop: 12, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Прикрепить листы CRS', 'Attach CRS sheets')}</span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{chosenSheets.length} {t(lang, 'выбрано', 'selected')}</span>
          </div>
          {availableSheets.map((s) => (
            <label key={s.id} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
              <Checkbox checked={sheets.has(s.id)} onChange={() => toggleSheet(s.id)} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)' }}>{s.id}</span>
              <CodeChip code={s.code} size="sm" />
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{s.doc} · {t(lang, 'рев.', 'rev.')} {revVer(s.rev, s.ver)}</span>
            </label>
          ))}
        </div>
      ) : null}
    </Dialog>
  )
}

// ---- Review (parallel review → approver's decision → CRS sheets → issue) ----
const ACT: Record<string, { ru: string; en: string; icon: string }> = {
  check: { ru: 'Входной контроль', en: 'Incoming check', icon: 'scan-search' },
  review: { ru: 'Рассмотрение', en: 'Review', icon: 'eye' },
  translate: { ru: 'Перевод замечаний', en: 'Translation', icon: 'type' },
  approve: { ru: 'Утверждение', en: 'Approval', icon: 'check-check' },
  issue: { ru: 'Выпуск TRM', en: 'Issue TRM', icon: 'send' },
}
/** Шаги, которые исполнитель закрывает сам. Совпадает с CLOSABLE_ACTS на
    сервере: волна плюс перевод, который открывается вместе с утверждением. */
const REVIEWING = ['check', 'review', 'translate']


/** Шаг маршрута рассмотрения — строка ответа GET /transmittals/{code}/review. */
interface ReviewStep {
  id: string
  ord: number
  role: string
  person: string
  assignee: string | null
  act: string
  state: 'done' | 'active' | 'wait'
  /** Считает сервер: шаг мой, если исполнитель — я или группа, в которой я состою. */
  mine?: boolean
  /** Кто закрыл шаг. У шага группы assignee называет группу, а не человека. */
  doneBy?: string | null
  date: string | null
  /** Ветка маршрута — дисциплина, за которую отвечает шаг. Пусто — шаг на
      весь пакет (утверждение, перевод, выпуск) либо маршрут по умолчанию. */
  scope?: string | null
  /** Срок шага по SLA матрицы; продлевается координатором ДО. */
  due?: string | null
  /** Исходный срок: продление его не двигает, и разница видна на экране. */
  dueBase?: string | null
}

/** Документ трансмиттала с итоговым кодом утверждающего. */
interface ReviewDoc {
  no: string
  title: string | null
  disc: string | null
  rev: string | null
  purpose: PurposeCode | null
  code: ReviewCode | null
  manual: boolean | null
  /** Незакрытые замечания по всем кругам: с ними документ не принимается в AFD. */
  openComments: number
}

/** Замечание рецензента. Кода у него нет: код — свойство документа. */
interface ReviewComment {
  id: string
  doc: string
  author: string
  page: number | null
  clause: string | null
  crit: string
  text: string
  /** Перевод замечания: его готовит шаг перевода, им печатается бланк CRS. */
  textEn?: string | null
  verdict: 'proposed' | 'accepted' | 'rejected'
  reason: string | null
  sheet: string | null
  date: string | null
  /** Авторство считает сервер: кнопки правки и снятия — только на своих. */
  mine?: boolean
  /* Замечание ПРОШЛОГО круга: сервер отдаёт признак с давних пор, а клиент его
     не читал — и кнопки правки предлагались на замечании, которого этот пакет
     не выпускал. Сервер отвечал 404. Кнопка, на которую сервер отказывает, —
     худший вариант из возможных. */
  earlier?: boolean
}

/** Лист CRS — печатная форма по одному документу. */
interface ReviewSheet {
  code: string
  doc: string
  rev: string | null
  /** Подача, по которой лист выпущен: адрес замечаний в нём. */
  ver: number | null
  reviewCode: ReviewCode
  status: 'draft' | 'issued'
  outgoing: string | null
}

/** Состояние рассмотрения целиком. Признаки перехода считает сервер: условие
    «можно готовить листы» и «можно выпускать» обязано жить в одном месте,
    иначе экран и база рано или поздно расходятся. */
interface ReviewState {
  id: string
  subject: string
  dir: string
  status: string
  sender: string
  recipient: string
  scope: string | null
  due: string | null
  purpose: PurposeCode | '—' | null
  /* Реестр, поданный этим трансмитталом. Заполнен — рассматривается реестр
     целиком, и замечания адресуются его строкам. */
  register: string | null
  steps: ReviewStep[]
  docs: ReviewDoc[]
  comments: Record<string, ReviewComment[]>
  sheets: ReviewSheet[]
  approver: string | null
  /** Имена для подсказок: кнопка обязана называть, кого ждать. */
  approverName: string | null
  issuerName: string | null
  /** Права вызвавшего — считает сервер тем же правилом, что отбивает команды:
      решения и листы — утверждающий; выпуск — исполнитель шага «Выпуск TRM». */
  mayDecide: boolean
  mayIssue: boolean
  materialized: boolean
  reviewDone: boolean
  canApprove: boolean
  canPrepare: boolean
  canIssue: boolean
  issued: boolean
  /** Отступления от маршрута: привлечение смежника и возврат на круг. */
  mayAddReviewer: boolean
  mayReturn: boolean
  /** Передача работы: свой шаг отдаёт исполнитель, чужой — координатор ДО. */
  mayReassign: boolean
  /** Досрочный возврат: критические замечания на входном контроле (шаг 12). */
  mayReturnEarly: boolean
  /* Остановка и перезапуск круга: считает сервер — тем же условием, каким
     отбивает сами команды (маршрут назначен, круг не выпущен, право
     администрирования). */
  mayResetReview: boolean
  /** Приёмка: пакет пришёл, входной контроль ещё не пройден, и право есть.
      Считает сервер — экран считал это по шагу маршрута, а шаги появляются
      только вместе с маршрутом, который сам и уводит статус из
      «Зарегистрирован»: панель приёмки не показывалась никогда. */
  mayCheckIn: boolean
}

/**
 * Панель перевода замечаний: русский текст рядом с полем английского.
 *
 * <p>Правит её исполнитель шага перевода, и это же условие отбивает сервер —
 * панель не спрашивает роль, а показывается по своему открытому шагу. Пустое
 * поле снимает перевод: бланк тогда снова печатает исходный текст.
 */
function TranslationPanel({ docs, commentsOf, lang, busy, onSave, onExport, onImport }: {
  docs: ReviewDoc[]
  commentsOf: (doc: string) => ReviewComment[]
  lang: Lang
  busy: boolean
  onSave: (crs: string, bodyEn: string) => void
  onExport: () => void
  onImport: (file: File) => void
}) {
  const [draft, setDraft] = useState<Record<string, string>>({})
  const sheetRef = useRef<HTMLInputElement>(null)
  /* Замечания ПРОШЛОГО круга сюда не попадают: их выпустило другое письмо,
     шаг перевода этого пакета их не переводит (403), а в выгруженном файле их
     нет — экран показывал бы строки, которых в файле не будет. */
  const rows = docs.flatMap((d) => commentsOf(d.no)
    .filter((c) => c.verdict !== 'rejected' && !c.earlier)
    .map((c) => ({ doc: d.no, c })))
  const left = rows.filter((r) => !r.c.textEn).length
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Перевод замечаний', 'Translation of findings')}</span>
        <Badge tone="accent" size="sm">{t(lang, 'шаг перевода', 'translation step')}</Badge>
        <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {left === 0
            ? t(lang, 'переведены все', 'everything is translated')
            : t(lang, `без перевода: ${left}`, `not translated: ${left}`)}
        </span>
        {/* Перевод пакетом. Построчно по каждому замечанию нажимать «Сохранить»
            — сорок запросов на сорок замечаний, а шаг перевода держит выпуск
            возвратного пакета: медленным становится весь круг, а не экран. */}
        <input ref={sheetRef} type="file" accept=".xlsx" style={{ display: 'none' }}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = '' }} />
        <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={13} />} disabled={busy || !rows.length}
                title={t(lang, 'Выгрузить замечания пакета одним файлом', 'Export the package findings as one file')}
                onClick={onExport}>{t(lang, 'Выгрузить', 'Export')}</Button>
        <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={13} />} disabled={busy}
                title={t(lang, 'Загрузить файл с переводом — разом по всем строкам', 'Upload the translated file — all rows at once')}
                onClick={() => sheetRef.current?.click()}>{t(lang, 'Загрузить перевод', 'Import translation')}</Button>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Замечаний нет — шаг перевода можно закрывать', 'No findings — the translation step can be closed')}
        </div>
      ) : rows.map(({ doc, c }) => (
        <div key={c.id} style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)' }}>{c.id}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{doc}</span>
            <CritBadge level={c.crit} lang={lang} />
            {c.textEn ? <Badge tone="success" size="sm">{t(lang, 'переведено', 'translated')}</Badge> : null}
          </div>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', margin: '4px 0 6px' }}>{c.text}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input style={{ flex: 1 }}
                   placeholder={t(lang, 'Текст замечания на английском — уйдёт в бланк CRS', 'Finding in English — goes to the CRS blank')}
                   value={draft[c.id] ?? c.textEn ?? ''}
                   onChange={(e) => setDraft((d) => ({ ...d, [c.id]: e.target.value }))} />
            <Button variant="secondary" size="sm" disabled={busy || (draft[c.id] ?? c.textEn ?? '') === (c.textEn ?? '')}
                    onClick={() => onSave(c.id, draft[c.id] ?? '')}>{t(lang, 'Сохранить', 'Save')}</Button>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Панель входного контроля: итог по каждой строке пакета.
 *
 * <p>Возврат был один на всю операцию — одна свободная фраза на сорок строк, —
 * и подрядчик, которому вернули четыре, не знал, что именно исправлять.
 * Основание берётся из справочника (иначе отчёт «сколько вернули и по каким
 * основаниям» снова не собрать), комментарий уточняет его словами.
 */
function IntakePanel({ docs, lang, busy, onSubmit }: {
  docs: ReviewDoc[]
  lang: Lang
  busy: boolean
  onSubmit: (returned: { docNo: string; reason: string; note: string | null }[]) => void
}) {
  const reasons = useSub<{ rows: { code: string; name: string }[] }>('/transmittals/intake-reasons')
  const [draft, setDraft] = useState<Record<string, { reason: string; note: string }>>({})
  const returned = docs
    .map((d) => ({ no: d.no, row: draft[d.no] }))
    .filter((r): r is { no: string; row: { reason: string; note: string } } => Boolean(r.row?.reason))
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Входной контроль', 'Incoming check')}</span>
        <Badge tone="accent" size="sm">{t(lang, 'построчно', 'row by row')}</Badge>
        <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {returned.length === 0
            ? t(lang, 'принимается весь состав', 'the whole package is accepted')
            : t(lang, `возвращается ${returned.length} из ${docs.length}`, `returning ${returned.length} of ${docs.length}`)}
        </span>
      </div>
      {docs.map((d) => (
        <div key={d.no} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' }}>
          <DiscTag code={d.disc ?? ''} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{d.no}</span>
          <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title ?? ''}</span>
          <Select size="sm" block={false} style={{ width: 210 }} disabled={busy}
                  value={draft[d.no]?.reason ?? ''}
                  onChange={(e) => setDraft((s) => ({ ...s, [d.no]: { reason: e.target.value, note: s[d.no]?.note ?? '' } }))}>
            <option value="">{t(lang, 'принята', 'accepted')}</option>
            {(reasons.data?.rows ?? []).map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
          </Select>
          <Input style={{ width: 260 }} disabled={busy || !draft[d.no]?.reason}
                 placeholder={t(lang, 'комментарий к основанию', 'comment on the ground')}
                 value={draft[d.no]?.note ?? ''}
                 onChange={(e) => setDraft((s) => ({ ...s, [d.no]: { reason: s[d.no]?.reason ?? '', note: e.target.value } }))} />
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderTop: '1px solid var(--border-subtle)' }}>
        <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Возвращённые строки уходят из состава: подрядчик увидит основание и комментарий по каждой',
                   'Returned rows leave the package: the contractor sees the ground and the comment for each')}
        </span>
        <Button variant="primary" size="sm" style={{ marginLeft: 'auto' }} disabled={busy}
                iconLeft={<Icon name="scan-search" size={14} />}
                onClick={() => onSubmit(returned.map((r) => ({
                  docNo: r.no,
                  reason: r.row.reason,
                  note: r.row.note.trim() || null,
                })))}>
          {returned.length === 0
            ? t(lang, 'Принять пакет целиком', 'Accept the whole package')
            : t(lang, `Завершить: вернуть ${returned.length}`, `Finish: return ${returned.length}`)}
        </Button>
      </div>
    </div>
  )
}

/** Строка справочника исполнителей: сотрудник проекта или группа. */
interface ReviewerOption {
  code: string
  name: string
  kind: 'user' | 'group'
  role: string | null
  disc: string[]
  members?: number
}

/** Ответ команды выпуска: номер исходящего, итоговый код и число листов. */
interface IssueResult {
  outgoing: string
  code: ReviewCode
  sheets: number
}

/** Размер очереди рассмотрения. Сервер отдаёт ровно эту порцию журнала. */
const QUEUE_SIZE = 50

/**
 * Очередь рассмотрения по стадиям круга.
 *
 * <p>В одном списке лежали пакеты трёх разных судеб: те, по которым круг ещё
 * не начат, те, где рецензенты работают, и давно закрытые. На проекте это
 * десятки строк, и «что мне делать сейчас» из них не читалось.
 *
 * <p>Границы — по статусу пакета (V80): {@code Зарегистрирован/Проверен/Принят}
 * означают, что волна ещё не открыта; {@code На рассмотрении/Замечания} — что
 * она идёт; остальное — что круг окончен. Отбор идёт НА СЕРВЕР, а не по
 * загруженной странице: очередь постраничная, и отбор по полусотне строк
 * показывал бы «сколько поместилось».
 */
const QUEUE_STAGES: Record<string, string[]> = {
  start: ['Зарегистрирован', 'Проверен', 'Принят'],
  running: ['На рассмотрении', 'Замечания'],
  done: ['Отправлен', 'Закрыт', 'Отклонён', 'Аннулирован'],
}

/* Команды рассмотрения. Сбрасывают один и тот же набор: даже одно замечание
   меняет не только себя — сервер пересчитывает признаки перехода, а выпуск
   заодно переписывает журнал трансмитталов, коды документов и листы. Что
   именно изменилось, клиент не знает и предсказывать не должен. */
function useReviewCommands(code: string) {
  const path = `/transmittals/${encodeURIComponent(code)}/review`
  const invalidate = () => [
    qk.sub(path),
    qk.all('transmittals'),
    qk.all('docs'),
    qk.all('crs'),
    qk.all('audit'),
    /* Очередь «Мои действия» и её счётчик — парой: закрытый шаг уходил из
       списка, а число на пункте навигации оставалось прежним. */
    ...inboxKeys,
  ]
  const assign = useCommand<void>({ path: () => path, invalidate })
  const comment = useCommand<{ docNo: string; page: number | null; crit: string; clause: string | null; body: string; rowRef?: string; kind?: string; needsAnswer?: boolean }>({
    path: () => `${path}/comments`,
    body: (a) => a,
    invalidate,
  })
  const withdraw = useCommand<{ crs: string }>({
    path: (a) => `${path}/comments/${encodeURIComponent(a.crs)}/withdraw`,
    invalidate,
  })
  const edit = useCommand<{ crs: string; crit: string; clause: string | null; body: string }>({
    method: 'PUT',
    path: (a) => `${path}/comments/${encodeURIComponent(a.crs)}`,
    body: (a) => ({ crit: a.crit, clause: a.clause, body: a.body }),
    invalidate,
  })
  const reject = useCommand<{ crs: string; reason: string }>({
    path: (a) => `${path}/comments/${encodeURIComponent(a.crs)}/reject`,
    body: (a) => ({ reason: a.reason }),
    invalidate,
  })
  const accept = useCommand<{ crs: string }>({
    path: (a) => `${path}/comments/${encodeURIComponent(a.crs)}/accept`,
    invalidate,
  })
  const finish = useCommand<{ step: string }>({
    path: (a) => `${path}/steps/${encodeURIComponent(a.step)}/finish`,
    invalidate,
  })
  const setCode = useCommand<{ doc: string; code: ReviewCode }>({
    method: 'PUT',
    path: (a) => `${path}/docs/${encodeURIComponent(a.doc)}/code`,
    body: (a) => ({ code: a.code }),
    invalidate,
  })
  /* Код сразу всем непрокодированным: пакет подрядчика — сорок строк, и сорок
     одинаковых решений в сорок приёмов это работа руками, а не решение.
     Команда была на сервере, а нажать её было негде. */
  const setCodes = useCommand<{ docNos: string[]; code: ReviewCode }>({
    method: 'PUT',
    path: () => `${path}/codes`,
    body: (a) => a,
    invalidate,
  })
  /* Привлечь смежника и вернуть на круг — разовые отступления от маршрута:
     матрица ими не правится, иначе следующий трансмиттал унаследовал бы
     случайного человека. */
  /* Передача шага: человек в отпуске или уволился, а пакет стоит. Меняется
     исполнитель шага, маршрут остаётся прежним. */
  const reassign = useCommand<{ step: string; assignee: string }>({
    method: 'PUT',
    path: (a) => `${path}/steps/${encodeURIComponent(a.step)}/assignee`,
    body: (a) => ({ assignee: a.assignee }),
    invalidate,
  })
  /* Продление срока шага: срок ставился по SLA при открытии и дальше не
     двигался ничем, а пакет в тридцать томов вместо трёх — обычное дело. */
  const extendStep = useCommand<{ step: string; due: string; reason: string }>({
    method: 'PUT',
    path: (a) => `${path}/steps/${encodeURIComponent(a.step)}/due`,
    body: (a) => ({ due: a.due, reason: a.reason }),
    invalidate: () => [qk.sub(path), ...inboxKeys],
  })
  const addReviewer = useCommand<{ assignee: string; role?: string }>({
    path: () => `${path}/reviewers`,
    body: (a) => a,
    invalidate,
  })
  const returnToReview = useCommand<{ assignee: string; reason: string }>({
    path: () => `${path}/return`,
    body: (a) => a,
    invalidate,
  })
  /* Двери администратора: круг, назначенный не тем маршрутом, доигрывали до
     конца либо аннулировали письмо целиком. Перезапуск возвращает круг на
     первый шаг, остановка снимает маршрут совсем. Причина обязательна — она
     уходит в журнал. */
  const restartReview = useCommand<{ reason: string }>({
    path: () => `${path}/restart`,
    body: (a) => a,
    invalidate,
  })
  const stopReview = useCommand<{ reason: string }>({
    path: () => `${path}/stop`,
    body: (a) => a,
    invalidate,
  })
  const prepare = useCommand<void>({ path: () => `${path}/sheets`, invalidate })
  const issue = useCommand<void, IssueResult>({ path: () => `${path}/issue`, invalidate })
  /* Перевод замечания. Путь свой, не под /review: замечание живёт само по
     себе, а право на перевод даёт открытый шаг маршрута — это проверяет
     сервер. */
  const translate = useCommand<{ crs: string; bodyEn: string }>({
    path: (a) => `/crs/${encodeURIComponent(a.crs)}/translation`,
    body: (a) => ({ bodyEn: a.bodyEn }),
    invalidate,
  })
  /* Досрочный возврат: пакет уходит обратно со входного контроля, не начиная
     круга. Ответ тот же, что у выпуска, — исходящий, код и число листов. */
  const returnEarly = useCommand<void, IssueResult>({ path: () => `${path}/return-early`, invalidate })
  /* Входной контроль. Путь не под /review: приёмка — команда трансмиттала, а
     не шага рассмотрения, и сбрасывает она то же самое. */
  const intake = useCommand<{ returned: { docNo: string; reason: string; note: string | null }[] },
    { status: string; returnTrm?: string }>({
    path: () => `/transmittals/${encodeURIComponent(code)}/register`,
    body: (a) => ({ returned: a.returned }),
    invalidate,
  })
  const busy = assign.isPending || comment.isPending || withdraw.isPending || edit.isPending || reject.isPending
    || accept.isPending || finish.isPending || setCode.isPending || prepare.isPending || issue.isPending
    || addReviewer.isPending || returnToReview.isPending || returnEarly.isPending || translate.isPending
    || reassign.isPending || setCodes.isPending || extendStep.isPending || intake.isPending
    || restartReview.isPending || stopReview.isPending
  return { assign, comment, withdraw, edit, reject, accept, finish, setCode, setCodes, prepare, issue,
           addReviewer, returnToReview, returnEarly, translate, reassign, extendStep, intake,
           restartReview, stopReview, busy, invalidate }
}

/**
 * Причина отказа для тоста: сами причины, а не только коды замечаний.
 *
 * <p>Причина у пропущенных строк чаще всего одна на всех («замечание не из
 * пакета» у чужого файла), и перечислять её сорок раз незачем. Разных причин
 * бывает две-три — тогда называются все.
 */
function skipReason(errors: { crs: string; reason: string }[]): string {
  const reasons = [...new Set(errors.map((e) => e.reason))]
  return reasons.slice(0, 2).join('; ') + (reasons.length > 2 ? '…' : '')
}

/* Почему кнопка недоступна. Недоступная кнопка без объяснения хуже отсутствия
   кнопки: пользователь жмёт и не понимает, чего системе не хватает. Первым
   называется «не ваша роль» — раньше координатор на шаге «Выпуск TRM» видел
   серую кнопку с бодрым текстом и считал её сломанной. */
function prepareHint(R: ReviewState, lang: Lang): string {
  if (R.issued) return t(lang, 'Возвратный TRM уже выпущен', 'The return TRM is already issued')
  if (!R.mayDecide) {
    const who = R.approverName ? ' — ' + R.approverName : ''
    return t(lang, `Листы CRS готовит утверждающий по матрице${who}`, `The matrix approver prepares the CRS sheets${who}`)
  }
  if (!R.materialized) return t(lang, 'Маршрут рассмотрения ещё не назначен', 'The review route is not assigned yet')
  if (!R.reviewDone) return t(lang, 'Рецензенты ещё не закрыли свои шаги', 'The reviewers have not finished yet')
  if (!R.canPrepare) return t(lang, 'Готовить нечего: у документов с принятыми замечаниями листы уже есть либо не проставлен код', 'Nothing to prepare: sheets exist already, or a document has no code')
  return t(lang, 'Лист собирает принятые замечания по документу', 'A sheet collects the accepted comments of one document')
}

function issueHint(R: ReviewState, lang: Lang): string {
  if (R.issued) return t(lang, 'Возвратный TRM уже выпущен', 'The return TRM is already issued')
  if (!R.mayIssue) {
    const who = R.issuerName ?? R.approverName
    const suffix = who ? ' — ' + who : ''
    return t(lang, `Возвратный TRM выпускает исполнитель шага «Выпуск TRM»${suffix}`, `The return TRM is issued by the executor of the Issue step${suffix}`)
  }
  if (!R.materialized) return t(lang, 'Маршрут рассмотрения ещё не назначен', 'The review route is not assigned yet')
  if (!R.reviewDone) return t(lang, 'Рецензенты ещё не закрыли свои шаги', 'The reviewers have not finished yet')
  if (!R.canIssue) return t(lang, 'Не у каждого документа есть код, либо не готов лист CRS', 'Some document has no code, or its CRS sheet is not prepared')
  return t(lang, 'Выпуск создаёт исходящий TRM подрядчику: коды документов и листы замечаний CRS', 'Issuing creates the outgoing TRM to the contractor: document codes plus the CRS sheets')
}

export function Review({ lang = 'ru', focus, onOpenViewer, onOpenDoc, onOpenTrm, onOpenCrs }: {
  lang?: Lang; focus?: string; onOpenViewer: (no: string) => void; onOpenDoc: (no: string) => void
  /** Открыть карточку трансмиттала: номер пакета в разборе — ссылка, а не подпись. */
  onOpenTrm?: (id: string) => void
  /** Открыть лист замечаний его экраном: код листа в разборе вёл в никуда. */
  onOpenCrs?: (code: string) => void
}) {
  /* Выбранный трансмиттал — часть адреса (/review/TRM-0042), а не отметка на
     window. Прежняя схема ставила поле, следующий экран его забирал и гасил:
     ссылку на разбор конкретного трансмиттала дать было нельзя, а возврат
     назад терял выбор. */
  const [active, setActive] = useState<string>(focus ?? '')
  useEffect(() => {
    if (focus) setActive(focus)
  }, [focus])

  /* Очередь — входящие трансмитталы с сервера. Выбор строки ничего не создаёт:
     состав рассмотрения появляется только по явной команде. */
  const [queueStage, setQueueStage] = useState('all')
  const queueQuery = useList<Transmittal>('transmittals', {
    page: 0,
    pageSize: QUEUE_SIZE,
    sort: [{ id: 'id', desc: false }],
    filters: { dir: ['in'], ...(QUEUE_STAGES[queueStage] ? { status: QUEUE_STAGES[queueStage] } : {}) },
  })
  const queue = queueQuery.data?.rows ?? []

  // Очередь может прийти пустой, поэтому выбор начинается пустым и встаёт на
  // первую строку только когда данные пришли.
  useEffect(() => {
    const first = queueQuery.data?.rows[0]
    if (active || !first) return
    setActive(first.id)
  }, [active, queueQuery.data])

  const reviewQuery = useSub<ReviewState>(active ? `/transmittals/${encodeURIComponent(active)}/review` : null)
  const R = reviewQuery.data
  const me = useMe()
  const discLabels = useDictLabels('disc')
  /* Значение кода рассмотрения — из справочника, а не из головы: «B» на разборе
     называют «принято с замечаниями», и это настраивает администратор. */
  const codeLabels = useDictLabels('code')
  /* Набор кодов — справочник проекта, а не три буквы: у одного заказчика
     A/B/C, у другого 1/2/3/4 (V71). Выключенные строки не предлагаются. */
  const codeRows = useDictRows('code').filter((r) => r.active)
  const cmd = useReviewCommands(active)
  /* Пачка перевода идёт мимо useCommand (это multipart), поэтому состояние
     рассмотрения сбрасывается вручную. */
  const qc = useQueryClient()
  /* Загрузка листа перевода идёт мимо useCommand (это форма, не JSON), и своей
     занятости у неё нет. Без неё кнопка оставалась живой во время запроса. */
  const [importing, setImporting] = useState(false)
  /* Состав поданного реестра: замечание пакета реестра адресуется его строке.
     Запрос идёт только у пакета реестра — у пакета документов адресат и есть
     сам документ. */
  const registerDocs = useList<{ no: string }>('docs',
    { page: 0, pageSize: 500, sort: [{ id: 'no', desc: false }], filters: { mdr: [R?.register ?? ''] } },
    { enabled: !!R?.register })
  const registerRows = (registerDocs.data?.rows ?? []).map((d) => d.no)

  const [commenting, setCommenting] = useState<string | null>(null)
  const [editing, setEditing] = useState<ReviewComment | null>(null)
  const [rejecting, setRejecting] = useState<ReviewComment | null>(null)
  /** Отступление от маршрута: 'add' — привлечь смежника, 'return' — вернуть на круг. */
  const [detour, setDetour] = useState<null | 'add' | 'return'>(null)
  /* Остановка и перезапуск круга: спрашивают причину, она уходит в журнал.
     Отдельным состоянием, а не через detour: тем диалогом выбирают человека,
     а этим — только объясняют отмену. */
  const [reset, setReset] = useState<null | 'restart' | 'stop'>(null)
  const [resetWhy, setResetWhy] = useState('')
  /* Передача шага: кому передаём — выбирают в том же диалоге, что и
     привлечение смежника, поэтому здесь хранится сам шаг, а не только режим. */
  const [handoff, setHandoff] = useState<ReviewStep | null>(null)
  /* Продление срока шага — своё окно: нужны дата и причина, а причина уходит в
     журнал и в разговор с подрядчиком. */
  const [extending, setExtending] = useState<ReviewStep | null>(null)
  const [printing, setPrinting] = useState<string | null>(null)

  /* Свой шаг рассмотрения. Их теперь несколько активных сразу, поэтому ищется
     именно свой, а не «текущий»: текущего больше нет.

     «Свой» считает сервер (флаг mine): шаг поручают и группе, а сравнение кода
     исполнителя со своим гасило бы кнопки её участникам. mine === undefined —
     совместимость с ответом без флага, там сравнение по коду. */
  const myId = me.data?.user.id ?? null
  const myStep = R?.steps.find((s) => s.state === 'active' && REVIEWING.includes(s.act)
    && (s.mine ?? s.assignee === myId)) ?? null
  /* Права считает сервер тем же правилом, что отбивает команды: решения и
     листы — утверждающий, выпуск — исполнитель шага «Выпуск TRM» из матрицы
     (без такого шага — снова утверждающий). Прежний клиентский пересчёт по
     approver давал координатору на шаге выпуска вечно серые кнопки. */
  const iApprove = R?.mayDecide ?? false
  /* Своя открытая подпись: цепочка утверждающих (руководитель дисциплины →
     ГИП) идёт по порядку, и следующая открывается, когда закрыта предыдущая.
     Прежде своей команды закрытия у утверждения не было вовсе. */
  const myApproval = R?.steps.find((s) => s.act === 'approve' && s.state === 'active'
    && (s.mine ?? s.assignee === myId)) ?? null
  const approvalsAhead = (R?.steps ?? []).filter((s) => s.act === 'approve' && s.state !== 'done').length > 1
  const iIssue = R?.mayIssue ?? false
  /* Маршрут назначается пакету, ПРОШЕДШЕМУ приёмку: «Зарегистрирован» сервер
     отбивает кодом review.not-checked (409). Признак нужен, чтобы не
     предлагать действие раньше входного контроля. */
  const notChecked = R?.status === 'Зарегистрирован'

  const docs = R?.docs ?? []
  const comments = R?.comments ?? {}
  const commentsOf = (doc: string) => comments[doc] ?? []
  /* «Ваше рассмотрение» показывает только собственные черновики: чужие тут
     раньше висели с кнопками, отвечавшими 403. mine !== false — совместимость
     с ответом без флага. */
  const mine = (doc: string) => commentsOf(doc).filter((c) => c.verdict === 'proposed' && c.mine !== false && !c.earlier)

  const reviewSteps = R?.steps.filter((s) => REVIEWING.includes(s.act)) ?? []
  const reviewDone = reviewSteps.filter((s) => s.state === 'done').length
  const coded = docs.filter((d) => d.code != null).length
  // Стадия выводится только из признаков сервера, а не из подсчёта строк.
  const stage = !R ? 1 : R.issued ? 4 : R.canIssue ? 3 : R.canApprove ? 2 : 1
  const STAGES = [
    { n: 1, ru: 'Параллельное рассмотрение', en: 'Parallel review', icon: 'eye' },
    { n: 2, ru: 'Решение утверждающего', en: "Approver's decision", icon: 'check-check' },
    { n: 3, ru: 'Выпуск возвратного TRM', en: 'Issue the return TRM', icon: 'send' },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Рассмотрение', 'Review')}</span>} title={t(lang, 'Рассмотрение по матрице распределения', 'Distribution-matrix review')} count={queueQuery.data ? queueQuery.data.total + t(lang, ' в очереди', ' queued') : null} />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Queue */}
        <div style={{ width: railWidth(300), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          <div style={{ padding: '10px 12px', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>{t(lang, 'Очередь рассмотрения', 'Review queue')}</div>
          {/* Стадия круга. Выбранный пакет не сбрасывается сменой отбора:
              разбор справа остаётся на месте, даже если строка ушла из списка. */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
            <Segmented size="sm" value={queueStage} onChange={setQueueStage} options={[
              { value: 'all', label: t(lang, 'Все', 'All') },
              { value: 'start', label: t(lang, 'Запустить', 'To start') },
              { value: 'running', label: t(lang, 'Идёт', 'Running') },
              { value: 'done', label: t(lang, 'Завершены', 'Done') },
            ]} />
          </div>
          {queueQuery.isError ? (
            <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить очередь', 'Failed to load the queue')}</div>
          ) : queue.length ? queue.map((r) => (
            <button key={r.id} onClick={() => setActive(r.id)} className="tdms-rail" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: r.id === active ? 'var(--accent-soft)' : 'transparent', boxShadow: r.id === active ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}>
              {/* Перенос по строкам: номер вида DP5-PST-CMK-TRM-0001 занимает
                  почти всю ширину колонки, и бейдж срока срезался. */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7 }}><DirChip dir={r.dir} /><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-strong)' }}>{r.id}</span><span style={{ marginLeft: 'auto' }}><SlaPill due={r.due} /></span></div>
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</div>
              {/* Статус берётся из строки журнала: без него закрытый трансмиттал
                  в очереди не отличить от ждущего рассмотрения. */}
              <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}><Purpose value={r.purpose} size="sm" /><TrmStatus s={r.status} /><span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{r.docs} док. · {r.from}</span></div>
            </button>
          )) : (
            <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{queueQuery.isPending
              /* Спиннер к тексту: голая надпись неотличима от зависания. */
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</span>
              : queueStage === 'all' ? t(lang, 'Очередь рассмотрения пуста', 'Review queue empty')
              : t(lang, 'В этой стадии пакетов нет', 'No packages at this stage')}</div>
          )}
        </div>
        {/* Detail */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          {!active ? (
            <EmptyState icon="check-check" title={t(lang, 'Выберите трансмиттал в очереди', 'Pick a transmittal from the queue')} />
          ) : reviewQuery.isPending ? (
            /* Спиннер к тексту: голая надпись неотличима от зависания. */
            <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size={13} />
              {t(lang, 'Загрузка…', 'Loading…')}
            </div>
          ) : reviewQuery.isError || !R ? (
            <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить рассмотрение', 'Failed to load the review')}</div>
          ) : (
          /* Рядом с очередью и меню правой колонке в 330 px оставалось на 1366
              меньше 450 на сам предмет рассмотрения. Обе колонки сжимаемы:
              minmax(0, 1fr) снимает у левой минимум по содержимому (иначе
              широкая карточка распирает сетку за край), правая ужимается до
              280, ниже которой список шагов маршрута не читается. */
          <div style={{ padding: 24, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 330px)', gap: 20, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* TRM header + stage tracker */}
              <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16 }}>
                {/* Перенос по строкам: номер, назначение, статус и строка
                    «от … · срок …» вместе требуют больше, чем колонка на 1366. */}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                  <span style={{ font: 'var(--font-view-title)', whiteSpace: 'nowrap' }}><CodeLink code={R.id} onOpen={onOpenTrm} /></span><Purpose value={R.purpose ?? '—'} />
                  <TrmStatus s={R.status} />
                  {R.issued ? <Badge tone="success" dot>{t(lang, 'Возвратный TRM выпущен', 'Return TRM issued')}</Badge> : null}
                  {/* Даты поступления в ответе нет, и выдумывать её нельзя: показываем отправителя и срок. */}
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'от', 'from')} {R.sender} · {t(lang, 'срок', 'due')} <SlaPill due={R.due} done={R.issued} /></span>
                </div>
                <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-md)', color: 'var(--text-body)', marginBottom: 14 }}>{R.subject}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
                  {STAGES.map((s, i) => {
                    const done = stage > s.n; const cur = stage === s.n
                    return (
                      <Fragment key={s.n}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, borderRadius: 999, flex: '0 0 auto', background: done ? 'var(--green-500)' : cur ? 'var(--accent)' : 'var(--surface-sunken)', color: done || cur ? '#fff' : 'var(--text-subtle)', border: '1px solid ' + (done ? 'var(--green-500)' : cur ? 'var(--accent)' : 'var(--border-strong)') }}>{done ? <Icon name="check" size={13} /> : <Icon name={s.icon} size={13} />}</span>
                          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: cur ? 600 : 400, color: cur ? 'var(--accent-ink)' : done ? 'var(--text-body)' : 'var(--text-subtle)' }}>{t(lang, s.ru, s.en)}</span>
                        </div>
                        {i < STAGES.length - 1 ? <span style={{ flex: 1, height: 2, margin: '0 12px', background: stage > s.n ? 'var(--green-400)' : 'var(--border-default)' }} /> : null}
                      </Fragment>
                    )
                  })}
                </div>
              </div>

              {/* Documents under review */}
              <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Документы под рассмотрением', 'Documents under review')}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{docs.length}</span>
                  <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'с кодом', 'coded')} {coded}/{docs.length}</span>
                  {/* Код всем непрокодированным разом: пакет подрядчика — сорок
                      строк, и сорок одинаковых решений в сорок приёмов это
                      работа руками, а не решение. */}
                  {iApprove && R.canApprove && coded < docs.length ? (
                    <Select size="sm" block={false} style={{ width: 150 }} value=""
                            disabled={cmd.busy}
                            title={t(lang, 'Поставить код всем документам без кода', 'Set the code for every uncoded document')}
                            onChange={(e) => {
                              const code = e.target.value as ReviewCode | ''
                              if (!code) return
                              const rest = docs.filter((d) => !d.code).map((d) => d.no)
                              if (!rest.length) return
                              cmd.setCodes.mutate({ docNos: rest, code }, {
                                onSuccess: () => toast({ tone: 'success', title: t(lang, 'Коды проставлены', 'Codes set'),
                                                         text: `${code} · ${rest.length}` }),
                                onError: (err) => toast({ tone: 'danger', title: t(lang, 'Не проставлены', 'Not set'), text: err.message }),
                              })
                            }}>
                      <option value="">{t(lang, 'всем без кода…', 'all uncoded…')}</option>
                      {codeRows.map((c) => <option key={c.code} value={c.code}>{c.name ? `${c.code} — ${c.name}` : c.code}</option>)}
                    </Select>
                  ) : null}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 0, padding: '7px 14px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-subtle)' }}>
                  {[t(lang, 'Документ', 'Document'), t(lang, 'Замечания', 'Comments'), t(lang, 'Код', 'Code'), ''].map((h, i) => <span key={i} style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', textAlign: i === 0 ? 'left' : 'center', paddingRight: i === 1 ? 14 : 0 }}>{h}</span>)}
                </div>
                {docs.length ? docs.map((d) => {
                  /* Круг ТЕКУЩИЙ: замечания прошлых кругов показаны отдельно и
                     здесь не считаются — иначе сводка расходится с панелью. */
                  const all = commentsOf(d.no).filter((c) => !c.earlier)
                  const accepted = all.filter((c) => c.verdict === 'accepted').length
                  const rejected = all.filter((c) => c.verdict === 'rejected').length
                  const draft = all.filter((c) => c.verdict === 'proposed').length
                  return (
                    <div key={d.no} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', alignItems: 'center', gap: 0, padding: '11px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ minWidth: 0 }}>
                        {/* Настоящий <a>: подчёркивание при наведении (base.css) работает
                            только на теге ссылки, span его не получает. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><DiscTag code={d.disc ?? ''} /><a {...clickable(() => onOpenDoc(d.no))} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-link)', cursor: 'pointer' }}>{d.no}</a><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>рев. {d.rev ?? '—'}</span></div>
                        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{d.title ?? ''}</div>
                      </div>
                      {/* Счёт замечаний вместо голосов: голосов больше нет. */}
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, justifyContent: 'center', paddingRight: 14 }}>
                        {all.length ? (
                          <>
                            <Badge tone="success" size="sm">{t(lang, 'принято', 'accepted')} {accepted}</Badge>
                            {rejected ? <Badge tone="neutral" size="sm">{t(lang, 'снято', 'rejected')} {rejected}</Badge> : null}
                            {draft ? <Badge tone="warning" size="sm">{t(lang, 'черновых', 'draft')} {draft}</Badge> : null}
                          </>
                        ) : <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'нет замечаний', 'none')}</span>}
                      </div>
                      {/* Код ставит утверждающий. Остальным поле показано, но
                          не выдано: то же правило держит сервер. */}
                      <div style={{ padding: '0 10px', display: 'grid', justifyItems: 'center', gap: 2 }}>
                        <Select size="sm" value={d.code ?? ''} onChange={(e) => { const v = e.target.value as ReviewCode | ''; if (v) cmd.setCode.mutate({ doc: d.no, code: v }) }} block={false} style={{ width: 70 }} disabled={R.issued || !iApprove || !R.canApprove || cmd.busy} title={iApprove ? (R.canApprove ? undefined : t(lang, 'Код ставится, когда рецензенты закончили', 'The code is set once the reviewers are done')) : t(lang, 'Код ставит утверждающий по матрице', 'The matrix approver sets the code')}><option value="" disabled>код…</option>{codeRows.map((c) => <option key={c.code} value={c.code} disabled={c.meaning === 'afd' && (d.openComments ?? 0) > 0}>{c.name ? `${c.code} — ${c.name}` : c.code}</option>)}</Select>
                        {/* Что означает поставленный код — из справочника
                            «Коды рассмотрения»: в чипе помещается буква, а
                            спорят о значении. */}
                        {d.code && codeLabels[d.code] ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', textAlign: 'center' }}>{codeLabels[d.code]}</span> : null}
                        {/* Почему «принято в работу» недоступно: выпуск отбивает
                            такой код при незакрытых замечаниях, и узнавать об
                            этом в самом конце — после подготовки листов — поздно. */}
                        {iApprove && (d.openComments ?? 0) > 0 ? (
                          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', textAlign: 'center' }}>
                            {t(lang, `не принять: замечаний открыто ${d.openComments}`, `cannot accept: ${d.openComments} open comment(s)`)}
                          </span>
                        ) : null}
                      </div>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {/* Утверждающий с открытой подписью выпускает замечание
                            сам: сводя чужие, он вправе добавить своё — то же
                            правило держит сервер (myOpenStep). */}
                        {myApproval && !R.issued ? (
                          <Button variant="secondary" size="sm" iconLeft={<Icon name="message-square-plus" size={13} />} disabled={cmd.busy} onClick={() => setCommenting(d.no)}>{t(lang, 'Замечание', 'Comment')}</Button>
                        ) : null}
                        <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={13} />} onClick={() => onOpenViewer(d.no)}>{t(lang, 'Разметка', 'Markup')}</Button>
                      </span>
                    </div>
                  )
                }) : (
                  <div style={{ padding: '14px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'В трансмиттале нет документов', 'The transmittal has no documents')}</div>
                )}
              </div>

              {/* Входной контроль: пока пакет не проверен, итог ставится по
                  каждой строке. Показывать её или нет, решает сервер тем же
                  правилом, каким пускает саму приёмку: экран требовал ЕЩЁ и
                  открытого шага «check», а шаги создаёт назначение маршрута —
                  оно же уводит статус из «Зарегистрирован». Условие не
                  сходилось никогда, и входной контроль обходился штатной
                  кнопкой «Назначить состав». */}
              {R.mayCheckIn ? (
                <IntakePanel docs={docs} lang={lang} busy={cmd.busy}
                             onSubmit={(returned) => cmd.intake.mutate({ returned }, {
                               /* Возврат — это письмо с номером, и номер называется сразу:
                                  искать свежий пакет в журнале человек не должен. */
                               onSuccess: (res) => toast({
                                 tone: 'success',
                                 title: t(lang, 'Входной контроль пройден', 'Incoming check done'),
                                 text: returned.length === 0
                                   ? t(lang, `Пакет ${R.id} проверен целиком — отправьте его по матрице`,
                                             `Package ${R.id} checked in full — send it through the matrix`)
                                   : t(lang, `Возвращено строк: ${returned.length}; письмо ${res.returnTrm ?? ''}`,
                                             `Rows returned: ${returned.length}; letter ${res.returnTrm ?? ''}`),
                               }),
                             })} />
              ) : null}

              {/* Рассмотрение рецензента: замечания и закрытие своего шага. */}
              {myStep && !R.issued ? (
                <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Ваше рассмотрение', 'Your review')}</span>
                    <Badge tone="accent" size="sm">{t(lang, ACT[myStep.act]?.ru ?? myStep.act, ACT[myStep.act]?.en ?? myStep.act)}</Badge>
                    <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'кода документу рецензент не ставит', 'a reviewer does not set the document code')}</span>
                  </div>
                  {docs.map((d) => (
                    <div key={d.no} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <DiscTag code={d.disc ?? ''} name={discLabels[d.disc ?? '']} />
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{d.no}</span>
                        <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title ?? ''}</span>
                        <Button variant="secondary" size="sm" iconLeft={<Icon name="message-square-plus" size={13} />} disabled={cmd.busy} onClick={() => setCommenting(d.no)}>{t(lang, 'Замечание', 'Comment')}</Button>
                      </div>
                      {mine(d.no).length ? (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {mine(d.no).map((c) => (
                            <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)', flex: '0 0 auto' }}>{c.id}</span>
                              <CritBadge level={c.crit} lang={lang} />
                              {c.page != null ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{t(lang, 'лист', 'p.')} {c.page}</span> : null}
                              <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{c.text}</span>
                              <Button variant="ghost" size="sm" iconLeft={<Icon name="pencil" size={13} />} disabled={cmd.busy} title={t(lang, 'Исправить своё замечание, пока шаг не закрыт', 'Edit your comment while the step is open')} onClick={() => setEditing(c)}>{t(lang, 'Изменить', 'Edit')}</Button>
                              <Button variant="ghost" size="sm" iconLeft={<Icon name="trash-2" size={13} />} disabled={cmd.busy} title={t(lang, 'Снять своё замечание, пока шаг не закрыт', 'Withdraw your comment while the step is open')} onClick={() => cmd.withdraw.mutate({ crs: c.id })}>{t(lang, 'Снять', 'Withdraw')}</Button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
                    <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, '«Замечаний нет» — тоже итог: шаг закрывается и без них', 'No comments is a result too: the step closes either way')}</span>
                    <Button variant="primary" size="sm" style={{ marginLeft: 'auto' }} iconLeft={<Icon name="check" size={14} />} disabled={cmd.busy} title={t(lang, 'Замечания уйдут утверждающему, изменить их будет нельзя', 'Your comments go to the approver and can no longer be changed')} onClick={() => cmd.finish.mutate({ step: myStep.id })}>{t(lang, 'Завершить рассмотрение', 'Finish review')}</Button>
                  </div>
                </div>
              ) : null}

              {/* Решение утверждающего: все замечания разом, часть отклоняется. */}
              {/* Рабочее место переводчика: видно, пока открыт свой шаг перевода.
                  Отклонённые замечания сюда не попадают — они не уйдут ни в
                  лист, ни подрядчику, и переводить их незачем. */}
              {myStep?.act === 'translate' && !R.issued ? (
                <TranslationPanel
                  docs={docs}
                  commentsOf={commentsOf}
                  lang={lang}
                  busy={cmd.busy || importing}
                  onSave={(crs, bodyEn) => cmd.translate.mutate({ crs, bodyEn }, {
                    onSuccess: () => toast({ tone: 'success', title: t(lang, 'Перевод сохранён', 'Translation saved'), text: crs }),
                  })}
                  onExport={() => {
                    void saveFile(`/transmittals/${encodeURIComponent(active)}/translation-sheet`)
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Лист не выгружен', 'Export failed'),
                        text: e instanceof Error ? e.message : String(e) }))
                  }}
                  onImport={(file) => {
                    /* Отчёт ПОСТРОЧНЫЙ: файл заполняют вне системы, и одна
                       лишняя строка не должна отменять остальные переводы.
                       Отказ по правам — не строка отчёта, а 403 целиком: его
                       ловит .catch ниже.

                       Занятость своя: загрузка идёт мимо useCommand, и без неё
                       кнопка оставалась живой — второй щелчок слал второй
                       запрос с ДРУГИМ ключом идемпотентности, и человек читал
                       ответ «Переведено: 0» как «файл не принят». */
                    setImporting(true)
                    void uploadForm<{ translated: number; skipped: number; errors: { crs: string; reason: string }[] }>(
                      `/transmittals/${encodeURIComponent(active)}/translation-sheet`, file,
                      { idempotencyKey: crypto.randomUUID() })
                      .then((r) => {
                        cmd.invalidate().forEach((queryKey) => { void qc.invalidateQueries({ queryKey }) })
                        /* Причина, а не только код: «пропущено 40: CRS-1, CRS-2»
                           не говорит, что не так с файлом. */
                        toast({ tone: r.skipped ? 'danger' : 'success',
                          title: t(lang, `Переведено: ${r.translated}`, `Translated: ${r.translated}`),
                          text: r.skipped
                            ? t(lang, `пропущено ${r.skipped} — ${skipReason(r.errors)}`,
                                     `skipped ${r.skipped} — ${skipReason(r.errors)}`)
                            : undefined })
                      })
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Перевод не загружен', 'Import failed'),
                        text: e instanceof Error ? e.message : String(e) }))
                      .finally(() => setImporting(false))
                  }} />
              ) : null}

              {iApprove && R.canApprove && !R.issued ? (
                <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Замечания на решение', 'Comments to decide')}</span>
                    <Badge tone="accent" size="sm">{t(lang, 'утверждение', 'approval')}</Badge>
                    <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'принято по умолчанию — отклоняются отдельные', 'accepted by default — you reject the odd one')}</span>
                  </div>
                  {docs.map((d) => {
                    const all = commentsOf(d.no)
                    if (!all.length) return null
                    return (
                      <div key={d.no} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--surface-sunken)' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{d.no}</span>
                          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title ?? ''}</span>
                          <span style={{ marginLeft: 'auto' }}><CodeChip code={d.code} /></span>
                        </div>
                        {all.map((c) => (
                          <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', opacity: c.verdict === 'rejected' ? 0.6 : 1 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)' }}>{c.id}</span>
                                <CritBadge level={c.crit} lang={lang} />
                                {c.verdict === 'rejected' ? <Badge tone="neutral" size="sm">{t(lang, 'снято', 'rejected')}</Badge> : null}
                                <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{c.author}{c.page != null ? ` · ${t(lang, 'лист', 'p.')} ${c.page}` : ''}{c.clause ? ` · ${c.clause}` : ''}</span>
                              </div>
                              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 4, textDecoration: c.verdict === 'rejected' ? 'line-through' : 'none' }}>{c.text}</div>
                              {c.reason ? <div style={{ font: 'var(--font-caption)', color: 'var(--warning-ink)', marginTop: 3 }}>{t(lang, 'причина:', 'reason:')} {c.reason}</div> : null}
                            </div>
                            {c.verdict === 'rejected' ? (
                              <Button variant="secondary" size="sm" iconLeft={<Icon name="rotate-ccw" size={13} />} disabled={cmd.busy} onClick={() => cmd.accept.mutate({ crs: c.id })}>{t(lang, 'Вернуть', 'Restore')}</Button>
                            ) : (
                              <>
                                {/* Лист уходит наружу за подписью утверждающего, и сводит
                                    замечания он: неудачную фразу раньше можно было только
                                    отклонить целиком, и рецензент писал замечание заново.
                                    Авторство при этом не подменяется — правку видно в журнале. */}
                                {/* Замечание ПРОШЛОГО круга этим пакетом не правится:
                                    его выпустило другое письмо, и сервер отвечает 404.
                                    Признак приходит с сервера давно, клиент его не читал. */}
                                {c.sheet || c.earlier ? null : (
                                  <Button variant="ghost" size="sm" iconLeft={<Icon name="pencil" size={13} />} disabled={cmd.busy}
                                          title={t(lang, 'Исправить формулировку до выпуска листа', 'Fix the wording before the sheet is issued')}
                                          onClick={() => setEditing(c)}>{t(lang, 'Формулировка', 'Wording')}</Button>
                                )}
                                {/* Отклонение — тоже команда ЭТОГО пакета: замечание прошлого
                                    круга выпустило другое письмо, и сервер отвечает 404. */}
                                {c.earlier ? (
                                  <Badge tone="neutral" size="sm">{t(lang, 'прошлый круг', 'earlier round')}</Badge>
                                ) : (
                                  <Button variant="secondary" size="sm" iconLeft={<Icon name="x" size={13} />} disabled={cmd.busy} title={t(lang, 'Отклонённое не уйдёт подрядчику и не попадёт в лист', 'A rejected comment never reaches the contractor or the sheet')} onClick={() => setRejecting(c)}>{t(lang, 'Отклонить', 'Reject')}</Button>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  })}
                  {docs.every((d) => !commentsOf(d.no).length) ? (
                    <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Рецензенты замечаний не оставили — остаётся проставить коды и выпустить возвратный TRM', 'The reviewers raised nothing — set the codes and issue the return TRM')}</div>
                  ) : null}
                </div>
              ) : null}

              {/* Листы CRS */}
              {R.sheets.length ? (
                <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Листы замечаний CRS', 'CRS sheets')}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{R.sheets.length}</span>
                  </div>
                  {R.sheets.map((s) => (
                    <div key={s.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                        <CodeLink code={s.code} onOpen={onOpenCrs} />
                      </span>
                      <CodeChip code={s.reviewCode} />
                      <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{s.doc} · рев. {revVer(s.rev, s.ver)}</span>
                      {s.status === 'issued'
                        ? <Badge tone="success" size="sm">{t(lang, 'в', 'in')} <CodeLink code={s.outgoing ?? ''} onOpen={s.outgoing ? onOpenTrm : undefined} /></Badge>
                        : <Badge tone="warning" size="sm">{t(lang, 'черновик', 'draft')}</Badge>}
                      <Button variant="secondary" size="sm" iconLeft={<Icon name="printer" size={13} />} onClick={() => setPrinting(s.code)}>{t(lang, 'Печать', 'Print')}</Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Distribution matrix — маршрут с сервера */}
            <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Матрица распределения', 'Distribution matrix')}</span>
                <Badge tone="info" size="sm">{t(lang, 'параллельно', 'parallel')}</Badge>
              </div>
              <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 14 }}>{t(lang, 'Рецензенты рассматривают одновременно, утверждающий решает по всем замечаниям', 'Reviewers run at the same time; the approver decides on every comment')} · {reviewDone}/{reviewSteps.length}</div>
              {/* Ветка маршрута названа у шага: на смешанном пакете волна
                  разворачивается по каждой дисциплине, и пять шагов «Входной
                  контроль · идёт» подряд отличались только фамилией
                  исполнителя. Дисциплину отдаёт сервер полем scope. */}
              {R.materialized ? R.steps.map((s, i) => (
                <MatrixStep key={s.id} done={s.state === 'done'} active={s.state === 'active'} icon={ACT[s.act]?.icon ?? 'circle'} act={t(lang, ACT[s.act]?.ru ?? s.act, ACT[s.act]?.en ?? s.act)}
                            role={s.scope ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <DiscTag code={s.scope} name={discLabels[s.scope]} />
                                <span>{s.role}</span>
                              </span>
                            ) : s.role}
                            person={s.person} date={s.date ?? undefined} last={i === R.steps.length - 1} lang={lang}
                            due={s.due ?? undefined} dueBase={s.dueBase ?? undefined}
                            onHandoff={s.state !== 'done' && !R.issued && (R.mayReassign || s.mine) ? () => setHandoff(s) : undefined}
                            onExtend={s.state !== 'done' && !R.issued && R.mayReassign ? () => setExtending(s) : undefined} />
              )) : (
                <div style={{ padding: '4px 0 2px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
                  {t(lang, 'Состав рассмотрения ещё не назначен. Маршрут берётся из матрицы распределения по области трансмиттала.', 'The review roster is not assigned yet. The route comes from the distribution matrix for the transmittal scope.')}
                  {/* Первое действие дня — приёмка, а панель приёмки лежит
                      ниже списка документов и на первом экране не видна. */}
                  {notChecked ? ' ' + t(lang, 'Сначала пакет проходит входной контроль — панель приёмки ниже, под списком документов.',
                                              'The package goes through the incoming check first — the intake panel is below the document list.') : ''}
                </div>
              )}

              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* До приёмки кнопка гаснет: сервер отвечал на неё 409, а
                    экран молчал — оператор жал её по нескольку раз. Отказ
                    сервера теперь тоже виден, как у соседних команд. */}
                {!R.materialized ? (
                  <Button variant="secondary" size="sm" block iconLeft={<Icon name="users" size={14} />} disabled={R.issued || notChecked || cmd.busy}
                          title={R.issued
                            ? t(lang, 'Возвратный TRM уже выпущен', 'The return TRM is already issued')
                            : notChecked
                              ? t(lang, 'Сначала входной контроль: комплектность, ревизии, исходники и штампы. Панель приёмки — ниже, под списком документов.',
                                        'Incoming check first: completeness, revisions, native files and stamps. The intake panel is below the document list.')
                              : t(lang, 'Состав назначается один раз, из матрицы распределения. Маршрут без утверждающего сервер отобьёт.', 'The roster is assigned once, from the matrix. A route without an approver is refused.')}
                          onClick={() => cmd.assign.mutate(undefined, {
                            onError: (e) => toast({ tone: 'danger', title: t(lang, 'Маршрут не назначен', 'Route not assigned'), text: e.message }),
                          })}>{t(lang, 'Назначить маршрут по матрице', 'Assign route from the matrix')}</Button>
                ) : null}
                {/* Отступления от маршрута видны только тем, кому сервер их
                    разрешит: флаги считает он же, тем условием, каким отбивает
                    команду. Кнопка, которую потом не пропустят, хуже её
                    отсутствия. */}
                {R.mayAddReviewer ? (
                  <Button variant="secondary" size="sm" block iconLeft={<Icon name="user-plus" size={14} />} disabled={cmd.busy}
                          title={t(lang, 'Добавить в идущее рассмотрение ещё одного исполнителя. Утверждение вернётся в ожидание, пока он не закроет свой шаг.', 'Add one more reviewer to the running review. Approval goes back to waiting until they finish.')}
                          onClick={() => setDetour('add')}>{t(lang, 'Привлечь рецензента', 'Pull in a reviewer')}</Button>
                ) : null}
                {R.mayReturn ? (
                  <Button variant="secondary" size="sm" block iconLeft={<Icon name="rotate-ccw" size={14} />} disabled={cmd.busy}
                          title={t(lang, 'Отправить на дополнительное рассмотрение с указанием причины', 'Send back for more review, stating the reason')}
                          onClick={() => setDetour('return')}>{t(lang, 'Вернуть на доп. рассмотрение', 'Return for more review')}</Button>
                ) : null}
                {/* Досрочный возврат виден только исполнителю входного контроля
                    и только при критическом замечании: обе половины условия
                    считает сервер, ими же он отбивает команду. */}
                {R.mayReturnEarly ? (
                  <Button variant="danger" size="sm" block iconLeft={<Icon name="arrow-down-left" size={14} />} disabled={cmd.busy}
                          title={t(lang, 'Вернуть пакет подрядчику, не начиная рассмотрения: документы уйдут на переиздание, замечания — листом CRS',
                                        'Send the package back without a review: documents go to reissue, findings go out as a CRS sheet')}
                          onClick={() => cmd.returnEarly.mutate(undefined, {
                            onSuccess: (res) => toast({
                              tone: 'success',
                              title: `${res.outgoing} ${t(lang, 'отправлен', 'sent')}`,
                              text: t(lang, `Пакет ${R.id} возвращён без рассмотрения · листов CRS ${res.sheets}`,
                                            `Package ${R.id} returned without a review · ${res.sheets} CRS sheet(s)`),
                            }),
                          })}>{t(lang, 'Вернуть без рассмотрения', 'Return without a review')}</Button>
                ) : null}
                {/* Двери администратора. Круг, назначенный не тем маршрутом,
                    доигрывали до конца либо аннулировали письмо целиком.
                    Признак считает сервер — тем же условием, каким отбивает
                    сами команды. */}
                {R.mayResetReview ? (
                  <>
                    <Button variant="secondary" size="sm" block iconLeft={<Icon name="rotate-ccw" size={14} />} disabled={cmd.busy}
                            title={t(lang, 'Вернуть круг на первый шаг: ход шагов снимается, маршрут и замечания остаются',
                                          'Take the review back to its first step: step progress is cleared, the route and the findings stay')}
                            onClick={() => { setResetWhy(''); setReset('restart') }}>
                      {t(lang, 'Перезапустить рассмотрение', 'Restart the review')}
                    </Button>
                    <Button variant="danger" size="sm" block iconLeft={<Icon name="square" size={14} />} disabled={cmd.busy}
                            title={t(lang, 'Снять маршрут совсем: состав назначается заново по исправленной матрице, замечания остаются',
                                          'Drop the route entirely: the roster is assigned again from the corrected matrix, the findings stay')}
                            onClick={() => { setResetWhy(''); setReset('stop') }}>
                      {t(lang, 'Остановить рассмотрение', 'Stop the review')}
                    </Button>
                  </>
                ) : null}
                {/* Подпись утверждающего: закрывает его шаг и открывает
                    следующий в цепочке. Пока цепочка одна, шаг закрывает и
                    выпуск — но тогда в журнале стоит имя выпускающего. */}
                {myApproval ? (
                  <Button variant="secondary" size="sm" block iconLeft={<Icon name="check-check" size={14} />} disabled={cmd.busy}
                          title={approvalsAhead
                            ? t(lang, 'Закрыть своё утверждение: пакет уйдёт следующему утверждающему', 'Close your approval: the package goes to the next approver')
                            : t(lang, 'Закрыть своё утверждение: коды проставлены, решение принято', 'Close your approval: codes are set and the decision is made')}
                          onClick={() => cmd.finish.mutate({ step: myApproval.id }, {
                            onSuccess: () => toast({ tone: 'success', title: t(lang, 'Утверждение закрыто', 'Approval closed'),
                                                     text: approvalsAhead ? t(lang, 'Пакет ушёл следующему утверждающему', 'The package went to the next approver') : undefined }),
                            onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не закрыто', 'Not closed'), text: e.message }),
                          })}>{t(lang, 'Закрыть утверждение', 'Close approval')}</Button>
                ) : null}
                <Button variant="secondary" size="sm" block iconLeft={<Icon name="file-text" size={14} />} disabled={!R.canPrepare || !iApprove || cmd.busy} title={prepareHint(R, lang)} onClick={() => cmd.prepare.mutate()}>{t(lang, 'Подготовить листы CRS', 'Prepare CRS sheets')}</Button>
                <Button variant="primary" size="sm" block iconLeft={<Icon name="send" size={14} />} disabled={!R.canIssue || !iIssue || cmd.busy} title={issueHint(R, lang)} onClick={() => cmd.issue.mutate(undefined, {
                  onSuccess: (res) => toast({
                    tone: 'success',
                    title: `${res.outgoing} ${t(lang, 'отправлен', 'sent')}`,
                    text: t(lang, `Возвратный TRM по ${R.id} · код ${res.code} · листов CRS ${res.sheets}`, `Return TRM for ${R.id} · code ${res.code} · ${res.sheets} CRS sheet(s)`),
                  }),
                })}>{R.issued ? t(lang, 'Возвратный TRM выпущен', 'Return TRM issued') : t(lang, 'Выпустить возвратный TRM', 'Issue the return TRM')}</Button>
                {/* Что делает выпуск — постоянной строкой, а не только тултипом:
                    «возврат» без пояснения читался как неизвестный объект. */}
                <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'Выпуск создаёт исходящий трансмиттал подрядчику: коды документов и листы замечаний CRS. Печатная форма листа — кнопкой «Печать» выше.', 'Issuing creates the outgoing transmittal to the contractor: document codes and CRS sheets. Print a sheet with the Print button above.')}</div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      <CommentDialog
        open={commenting != null || editing != null}
        doc={commenting ?? editing?.doc ?? ''}
        initial={editing}
        rows={R?.register ? registerRows : undefined}
        lang={lang}
        busy={cmd.busy}
        onClose={() => { setCommenting(null); setEditing(null) }}
        onSubmit={(body) => {
          if (editing) cmd.edit.mutate({ crs: editing.id, crit: body.crit, clause: body.clause, body: body.body }, { onSuccess: () => setEditing(null) })
          else cmd.comment.mutate({ docNo: commenting ?? '', ...body }, { onSuccess: () => setCommenting(null) })
        }}
      />
      <RejectDialog
        crs={rejecting}
        lang={lang}
        busy={cmd.busy}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => rejecting && cmd.reject.mutate({ crs: rejecting.id, reason }, { onSuccess: () => setRejecting(null) })}
      />
      <DetourDialog
        mode={handoff ? 'handoff' : detour}
        step={handoff}
        lang={lang}
        busy={cmd.busy}
        steps={R?.steps ?? []}
        onClose={() => { setDetour(null); setHandoff(null) }}
        onSubmit={(body) => {
          if (handoff) {
            cmd.reassign.mutate({ step: handoff.id, assignee: body.assignee }, {
              onSuccess: () => { setHandoff(null); toast({ tone: 'success', title: t(lang, 'Шаг передан', 'Step handed over'), text: body.assignee }) },
            })
          } else if (detour === 'return') {
            cmd.returnToReview.mutate({ assignee: body.assignee, reason: body.reason }, {
              onSuccess: () => { setDetour(null); toast({ tone: 'success', title: t(lang, 'Возвращено на рассмотрение', 'Returned for more review'), text: body.assignee }) },
            })
          } else {
            cmd.addReviewer.mutate({ assignee: body.assignee, role: body.role }, {
              onSuccess: () => { setDetour(null); toast({ tone: 'success', title: t(lang, 'Рецензент привлечён', 'Reviewer pulled in'), text: body.assignee }) },
            })
          }
        }}
      />
      {/* Остановка и перезапуск: диалог один на обе двери — они различаются
          не порядком действий, а тем, что остаётся от маршрута. */}
      <Dialog open={reset != null} onClose={() => setReset(null)} sizeKey="review-reset" width={480}
              title={reset === 'stop'
                ? t(lang, 'Остановить рассмотрение', 'Stop the review')
                : t(lang, 'Перезапустить рассмотрение', 'Restart the review')}
              subtitle={R?.id}>
        <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          {reset === 'stop'
            ? t(lang, 'Маршрут снимается целиком: состав назначается заново, уже по исправленной матрице. Замечания остаются — их писал рецензент.',
                     'The route is dropped entirely: the roster is assigned again from the corrected matrix. The findings stay: a reviewer wrote them.')
            : t(lang, 'Круг возвращается на первый шаг: ход шагов снимается, маршрут и замечания остаются на месте.',
                     'The review goes back to its first step: step progress is cleared, the route and the findings stay.')}
        </div>
        <Input label={t(lang, 'Причина', 'Reason')} size="sm" style={{ marginTop: 14 }} value={resetWhy}
               onChange={(e) => setResetWhy(e.target.value)}
               placeholder={t(lang, 'напр. маршрут назначен не по той матрице', 'e.g. the route came from the wrong matrix')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setReset(null)}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant={reset === 'stop' ? 'danger' : 'primary'} disabled={resetWhy.trim() === '' || cmd.busy}
                  title={resetWhy.trim() ? undefined : t(lang, 'Причина обязательна', 'A reason is required')}
                  onClick={() => {
                    const command = reset === 'stop' ? cmd.stopReview : cmd.restartReview
                    const done = reset === 'stop'
                      ? t(lang, 'Рассмотрение остановлено', 'Review stopped')
                      : t(lang, 'Рассмотрение перезапущено', 'Review restarted')
                    command.mutate({ reason: resetWhy.trim() }, {
                      onSuccess: () => { setReset(null); toast({ tone: 'success', title: done, text: R?.id }) },
                      onError: (e) => toast({ tone: 'danger', title: done, text: e.message }),
                    })
                  }}>
            {reset === 'stop' ? t(lang, 'Остановить', 'Stop') : t(lang, 'Перезапустить', 'Restart')}
          </Button>
        </div>
      </Dialog>
      <ExtendDueDialog
        step={extending}
        lang={lang}
        busy={cmd.busy}
        onClose={() => setExtending(null)}
        onSubmit={(due, reason) => extending && cmd.extendStep.mutate({ step: extending.id, due, reason }, {
          onSuccess: () => { setExtending(null); toast({ tone: 'success', title: t(lang, 'Срок продлён', 'Deadline extended'), text: due }) },
          onError: (e: Error) => toast({ tone: 'danger', title: t(lang, 'Не продлён', 'Not extended'), text: e.message }),
        })}
      />
      <SheetPrint code={printing} lang={lang} onClose={() => setPrinting(null)} />
    </div>
  )
}

/**
 * Продление срока шага рассмотрения.
 *
 * <p>Причина обязательна: продление — предмет разговора с подрядчиком, и
 * «почему рассмотрение шло месяц» спрашивают через полгода. Дату в прошлое
 * сервер не примет — это не продление, а способ зажечь просрочку.
 */
function ExtendDueDialog({ step, lang, busy, onClose, onSubmit }: {
  step: ReviewStep | null
  lang: Lang
  busy?: boolean
  onClose: () => void
  onSubmit: (due: string, reason: string) => void
}) {
  const [due, setDue] = useState('')
  const [reason, setReason] = useState('')
  useEffect(() => { if (step) { setDue(''); setReason('') } }, [step])
  if (!step) return null
  return (
    <Dialog open onClose={onClose} title={t(lang, 'Продлить срок шага', 'Extend the step deadline')}>
      <div style={{ width: 420, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {step.role} · {step.person}
          {step.due ? ` · ${t(lang, 'сейчас до', 'now by')} ${step.due}` : ''}
        </div>
        <DateInput label={t(lang, 'Новый срок', 'New deadline')} value={due} onChange={setDue}
                   minDate={new Date()} lang={lang} />
        <Input label={t(lang, 'Причина', 'Reason')} value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'например: пакет в тридцать томов вместо трёх', 'e.g. thirty volumes instead of three')} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={busy || !due || !reason.trim()}
                  title={due && reason.trim() ? undefined : t(lang, 'Нужны дата и причина', 'Date and reason are required')}
                  onClick={() => onSubmit(due, reason.trim())}>{t(lang, 'Продлить', 'Extend')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

/* Замечание рецензента. Ни кода, ни срока, ни дисциплины здесь нет намеренно:
   их проставляет сервер по документу, а посчитанное на клиенте рано или поздно
   разойдётся с базой. Тот же диалог правит своё замечание (initial): форма
   одна, различие — заголовок, кнопка и запертый лист. */
function CommentDialog({ open, doc, initial = null, rows, lang, busy, onClose, onSubmit }: {
  open: boolean
  doc: string
  /** Правка существующего замечания: поля заполняются из него. */
  initial?: ReviewComment | null
  /* Строки реестра. Пусто — рассматривается документ, и замечание адресуется
     ему целиком; у пакета реестра предмет замечания — позиция. */
  rows?: string[]
  lang: Lang
  busy: boolean
  onClose: () => void
  onSubmit: (body: { page: number | null; crit: string; clause: string | null; body: string; rowRef?: string
                     kind?: string; needsAnswer?: boolean }) => void
}) {
  const [page, setPage] = useState('')
  const [crit, setCrit] = useState('med')
  const [clause, setClause] = useState('')
  const [text, setText] = useState('')
  const [rowRef, setRowRef] = useState('')
  /* Вид замечания и «ответа не ждём». Критичность отвечает «насколько срочно»,
     а не «какого рода»: техническое, оформление, комплектность и справочное
     разбирают разными руками, а справочное ответа не требует вовсе. */
  const [kind, setKind] = useState('')
  const [needsAnswer, setNeedsAnswer] = useState(true)
  const kindRows = useDictRows('crsKind')

  useEffect(() => {
    if (!open) return
    setPage(initial?.page != null ? String(initial.page) : '')
    setCrit(initial?.crit ?? 'med')
    setClause(initial?.clause ?? '')
    setText(initial?.text ?? '')
    setRowRef('')
    setKind('')
    setNeedsAnswer(true)
  }, [open, initial])

  const ready = text.trim() !== ''
  return (
    <Dialog open={open} onClose={onClose} sizeKey="crs-comment" title={initial ? t(lang, 'Исправить замечание', 'Edit the comment') : t(lang, 'Замечание по документу', 'Comment on the document')} subtitle={initial ? `${doc} · ${initial.id}` : doc}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12 }}>
        {/* При правке лист заперт: метка разметки на чертеже привязана к нему,
            и смена листа из формы разъехалась бы с местом метки. */}
        {rows && rows.length ? (
          /* У пакета реестра замечание адресуется позиции: без этого подрядчик
             получил бы «в реестре что-то не так» без указания, что именно. */
          <div style={{ gridColumn: '1 / -1' }}>
            <Select label={t(lang, 'Строка реестра', 'Register row')} size="sm" value={rowRef} disabled={initial != null}
                    onChange={(e) => setRowRef(e.target.value)}>
              <option value="">{t(lang, '— реестр целиком —', '— the whole register —')}</option>
              {rows.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </div>
        ) : (
          <Input label={t(lang, 'Лист', 'Sheet')} size="sm" value={page} disabled={initial != null} onChange={(e) => setPage(e.target.value.replace(/\D/g, ''))} placeholder="—" />
        )}
        <Select label={t(lang, 'Критичность', 'Criticality')} size="sm" value={crit} onChange={(e) => setCrit(e.target.value)}>
          {Object.entries(CRIT_LABEL).map(([c, l]) => <option key={c} value={c}>{t(lang, l.ru, l.en)}</option>)}
        </Select>
        <Input label={t(lang, 'Пункт НТД', 'Clause')} size="sm" value={clause} onChange={(e) => setClause(e.target.value)} placeholder={t(lang, 'напр. ГОСТ 21.201, п. 5.2', 'e.g. ISO 128, cl. 5.2')} />
        {initial ? null : (
          <>
            <Select label={t(lang, 'Вид замечания', 'Comment kind')} size="sm" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t(lang, '— не указан —', '— not set —')}</option>
              {kindRows.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
            </Select>
            <label style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', alignSelf: 'end', paddingBottom: 6 }}
                   title={t(lang, 'Справочное замечание закрывается решением заказчика, напоминания по нему не идут',
                                 'An informational comment is closed by the owner; no reminders are sent')}>
              <input type="checkbox" checked={!needsAnswer} onChange={(e) => setNeedsAnswer(!e.target.checked)} />
              {t(lang, 'Ответ не требуется', 'No answer required')}
            </label>
          </>
        )}
      </div>
      <label style={{ display: 'block', marginTop: 14, marginBottom: 'var(--space-3)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)', color: 'var(--text-body)' }}>{t(lang, 'Текст замечания', 'Comment text')}</label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t(lang, 'Что именно не так и что требуется исправить…', 'What is wrong and what has to be fixed…')}
        style={{ width: '100%', minHeight: 96, padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', color: 'var(--text-body)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', resize: 'vertical', boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" iconLeft={<Icon name="check" size={14} />} disabled={!ready || busy}
                title={ready ? undefined : t(lang, 'Нужен текст замечания', 'The comment text is required')}
                onClick={() => onSubmit({ page: page ? Number(page) : null, crit, clause: clause.trim() || null,
                                          body: text.trim(), rowRef: rowRef || undefined,
                                          kind: kind || undefined, needsAnswer })}>
          {initial ? t(lang, 'Сохранить исправление', 'Save the edit') : t(lang, 'Выпустить замечание', 'Raise the comment')}
        </Button>
      </div>
    </Dialog>
  )
}

/* Отклонение без причины не объяснимо ни автору замечания, ни аудиту, поэтому
   причина здесь обязательна — и то же самое проверяет сервер. */
function RejectDialog({ crs, lang, busy, onClose, onSubmit }: {
  crs: ReviewComment | null
  lang: Lang
  busy: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  useEffect(() => { setReason('') }, [crs?.id])

  return (
    <Dialog open={crs != null} onClose={onClose} sizeKey="crs-reject" width={480} title={t(lang, 'Отклонить замечание', 'Reject the comment')} subtitle={crs?.id}>
      <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
        {crs?.text}
      </div>
      <Input label={t(lang, 'Причина', 'Reason')} size="sm" style={{ marginTop: 14 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t(lang, 'напр. учтено в другом замечании', 'e.g. covered by another comment')} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" iconLeft={<Icon name="x" size={14} />} disabled={reason.trim() === '' || busy}
                title={reason.trim() ? undefined : t(lang, 'Причина обязательна', 'A reason is required')}
                onClick={() => onSubmit(reason.trim())}>
          {t(lang, 'Отклонить', 'Reject')}
        </Button>
      </div>
    </Dialog>
  )
}

/**
 * Отступление от маршрута: привлечь смежника или вернуть на круг.
 *
 * <p>Форма одна на оба случая — в обоих называется исполнитель. Различие в
 * причине: возврат сдвигает срок всей партии, поэтому без неё команда не
 * уходит, а привлечение обходится пометкой роли.
 *
 * <p>Уже занятые в маршруте не предлагаются: второй шаг тому же человеку —
 * почти всегда промах по списку, а не замысел.
 */
function DetourDialog({ mode, step, lang, busy, steps, onClose, onSubmit }: {
  mode: null | 'add' | 'return' | 'handoff'
  /** Передаваемый шаг: его исполнителя из списка не убираем — его и меняют. */
  step?: ReviewStep | null
  lang: Lang
  busy: boolean
  steps: ReviewStep[]
  onClose: () => void
  onSubmit: (body: { assignee: string; role: string; reason: string }) => void
}) {
  const people = useMeta<ReviewerOption[]>('reviewers').data ?? []
  const [assignee, setAssignee] = useState('')
  const [role, setRole] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (mode == null) return
    setAssignee(''); setRole(''); setReason('')
  }, [mode])

  const taken = new Set(steps.filter((s) => s.state !== 'done').map((s) => s.assignee ?? ''))
  /* Кто уже занят на этом пакете и на каком шаге: при передаче он из списка
     не выпадает, но назван — иначе один человек оказывается на двух шагах
     не по замыслу, а по недосмотру. */
  const busyOn = new Map(steps.filter((s) => s.state !== 'done' && s.assignee)
    .map((s) => [s.assignee as string, s.role]))
  const ret = mode === 'return'
  const hand = mode === 'handoff'
  /* Занятых исключаем при привлечении и возврате: там второй шаг тому же
     человеку — почти всегда промах по списку. При ПЕРЕДАЧЕ так делать нельзя:
     на многодисциплинарном пакете маршрут занимает почти всю команду, и
     список сжимался до администратора системы, которому шаг рассмотрения не
     отдаст никто. Из него убирается только нынешний исполнитель шага, а
     первыми идут те, чья дисциплина совпала с веткой шага. */
  const suits = (o: ReviewerOption) => (step?.scope && o.disc.includes(step.scope) ? 0 : 1)
  const options = hand
    ? people.filter((o) => o.code !== (step?.assignee ?? '')).sort((a, b) => suits(a) - suits(b))
    : people.filter((o) => !taken.has(o.code))
  const ready = assignee !== '' && (!ret || reason.trim() !== '')

  return (
    <Dialog open={mode != null} onClose={onClose} sizeKey="review-detour" width={460}
            title={hand ? t(lang, 'Передать шаг', 'Hand the step over')
              : ret ? t(lang, 'Вернуть на дополнительное рассмотрение', 'Return for more review')
              : t(lang, 'Привлечь рецензента', 'Pull in a reviewer')}
            subtitle={hand
              ? `${step?.role ?? ''} · ${t(lang, 'сейчас', 'now')} ${step?.person ?? '—'}`
              : ret
              ? t(lang, 'Шаг утверждения вернётся в ожидание, пока названный не закроет свой', 'Approval goes back to waiting until the named person finishes')
              : t(lang, 'Добавляется разово, в этот трансмиттал: матрица распределения не меняется', 'Added once, to this transmittal only: the distribution matrix is untouched')}>
      <Select label={t(lang, 'Исполнитель', 'Assignee')} size="sm" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
        <option value="">{t(lang, '— выберите —', '— pick one —')}</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.name}{o.kind === 'group' ? ` · ${t(lang, 'группа', 'group')} (${o.members ?? 0})` : o.disc.length ? ` · ${o.disc.join(', ')}` : ''}
            {hand && busyOn.has(o.code) ? ` · ${t(lang, 'занят', 'busy')}: ${busyOn.get(o.code)}` : ''}
          </option>
        ))}
      </Select>
      {hand ? null : ret ? (
        <Input label={t(lang, 'Причина', 'Reason')} size="sm" style={{ marginTop: 14 }} value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'напр. нужна оценка по электрике', 'e.g. electrical assessment needed')} />
      ) : (
        <Input label={t(lang, 'Роль в рассмотрении', 'Role in the review')} size="sm" style={{ marginTop: 14 }} value={role} onChange={(e) => setRole(e.target.value)}
               placeholder={t(lang, 'Доп. рассмотрение', 'Additional review')} />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={!ready || busy}
                title={ready ? undefined : ret ? t(lang, 'Нужны исполнитель и причина', 'An assignee and a reason are required') : t(lang, 'Не выбран исполнитель', 'No assignee picked')}
                onClick={() => onSubmit({ assignee, role: role.trim(), reason: reason.trim() })}>
          {hand ? t(lang, 'Передать', 'Hand over') : ret ? t(lang, 'Вернуть', 'Return') : t(lang, 'Привлечь', 'Pull in')}
        </Button>
      </div>
    </Dialog>
  )
}

/** Печатная форма листа CRS — то, что уходит подрядчику с исходящим TRM. */

function MatrixStep({ done, active, icon, act, role, person, date, due, dueBase, last, lang, onHandoff, onExtend }: { done?: boolean; active?: boolean; icon: string; act: ReactNode; role: ReactNode; person: string; date?: string; due?: string; dueBase?: string; last?: boolean; lang: Lang; onHandoff?: () => void; onExtend?: () => void }) {
  return (
    <div style={{ position: 'relative', paddingLeft: 30, paddingBottom: last ? 0 : 14, paddingTop: 2 }}>
      {!last ? <span style={{ position: 'absolute', left: 11, top: 24, bottom: 0, width: 2, background: 'var(--border-default)' }} /> : null}
      <span style={{ position: 'absolute', left: 0, top: 1, width: 24, height: 24, display: 'grid', placeItems: 'center', borderRadius: 999, background: done ? 'var(--green-500)' : 'var(--surface-card)', border: '2px solid ' + (done ? 'var(--green-500)' : active ? 'var(--amber-400)' : 'var(--slate-300)'), color: done ? '#fff' : 'var(--text-muted)' }}>{done ? <Icon name="check" size={13} /> : <Icon name={icon} size={12} />}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>{act}</span>
        {active ? <Badge tone="accent" size="sm">{t(lang, 'идёт', 'in progress')}</Badge> : date ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-subtle)' }}>{date}</span> : null}
      </div>
      <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 1 }}>{role}</div>
      {/* Ряд переносится по строкам: колонка маршрута на 1366 отдаёт под него
          около 280 px, а исполнитель со сроком и двумя кнопками требует
          вдвое больше — «Продлить» обрезалось до «Про…». */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 5 }}>
        <Avatar name={person} size={18} /><span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{person}</span>
        {/* Передача работы: без неё пакет стоял до выхода человека из отпуска.
            Кнопка есть только там, где сервер команду пропустит. */}
        {onHandoff ? (
          <Button variant="ghost" size="sm" iconLeft={<Icon name="user-cog" size={13} />} onClick={onHandoff}
                  title={t(lang, 'Передать шаг другому исполнителю', 'Hand the step to someone else')}>{t(lang, 'Передать', 'Hand over')}</Button>
        ) : null}
        {/* Срок шага и его продление. Срок ставился по SLA матрицы при открытии
            и дальше не двигался ничем: оставалось смотреть, как шаг горит
            красным по дате, которую стороны уже пересогласовали. */}
        {due ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}
                title={t(lang, 'Срок шага', 'Step deadline')}>{t(lang, 'до', 'by')} {due}</span>
        ) : null}
        {/* Продление названо вслух: срок двигали, и разговор о задержке идёт
            об исходной дате, а не о последней. Без неё продление стирало
            просрочку отовсюду, как будто её не было. */}
        {due && dueBase && dueBase !== due ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--danger-ink)' }}
                title={t(lang, 'Исходный срок шага — от него считается отставание',
                              'The original deadline — the lag is counted from it')}>
            {t(lang, 'продлён с', 'extended from')} {dueBase}
          </span>
        ) : null}
        {onExtend ? (
          <Button variant="ghost" size="sm" iconLeft={<Icon name="calendar-days" size={13} />} onClick={onExtend}
                  title={t(lang, 'Продлить срок шага', 'Extend the step deadline')}>{t(lang, 'Продлить', 'Extend')}</Button>
        ) : null}
      </div>
    </div>
  )
}
